<template>
  <div
    v-if="result" :key="result" class="calculation-steps"
    :class="{ result: result }"
  >
    <h2 class="h4">
      Calculation Steps:
    </h2>
    <p>There are six steps you would follow when testing a hypothesis for the difference in means:</p>

    <ol>
      <!-- 1 -->
      <li>State the hypothesis:</li>
      <p class="formulas">
        \[H_0:\mu_0{{ calculationData.operator[0] }}{{ calculationData.expectedDifference }}\]
      </p>
      <p>And the alternative hypothesis is:</p>
      <p class="formulas">
        \[H_1:\mu_0{{ calculationData.operator[1] }}{{ calculationData.expectedDifference }}\]
      </p>
      <p v-if="currentTail === 'two-tailed'">
        Since <span class="inline-formulas">\[H_1\]</span> includes the possibility of a difference in both directions (greater than and less than the null hypothesis value), this is a <span class="bolded-texts">two-sided test</span>.
      </p>
      <p v-else>
        Since <span class="inline-formulas">\[H_1\]</span> specifies a difference in only one direction, <span v-if="currentTail === 'left-tailed'">less than the null hypothesis value</span><span v-if="currentTail === 'right-tailed'">greater than the null hypothesis value</span>, this is a <span class="bolded-texts">one-sided test</span>.
      </p>

      <!-- 2 -->
      <li>Select an appropriate test for your hypothesis:</li>
      <p v-if="calculationData.sampleType === 'Dependent' && calculationData.answer === 'Yes'">
        Since the two samples are <span class="bolded-texts">dependent</span>, the population standard deviation of paired differences is <span class="bolded-texts">known</span>, and the sample size is <span class="bolded-texts">large</span>, then we use the paired z-test:
      </p>
      <p v-if="calculationData.sampleType === 'Dependent' && calculationData.answer === 'No'">
        Since the two samples are <span class="bolded-texts">dependent</span> and the population standard deviation of paired differences is <span class="bolded-texts">unknown</span>, we use the paired t-test:
      </p>
      <p v-if="calculationData.sampleType === 'Independent' && calculationData.answer === 'Yes'">
        Since the two samples are <span class="bolded-texts">independent</span> and the population standard deviations of the two samples are <span class="bolded-texts">known</span>, we use the independent z-test:
      </p>
      <p v-if="calculationData.sampleType === 'Independent' && calculationData.answer === 'No' && calculationData.sampleAssumption === 'Equal'">
        Since the two samples are <span class="bolded-texts">independent</span> and the population standard deviations are <span class="bolded-texts">unknown</span> but assumed <span class="bolded-texts">equal</span>, we use the independent t-test:
      </p>
      <p v-if="calculationData.sampleType === 'Independent' && calculationData.answer === 'No' && calculationData.sampleAssumption === 'Unequal'">
        Since the two samples are <span class="bolded-texts">independent</span> and the population standard deviations are <span class="bolded-texts">unknown</span> and assumed to be <span class="bolded-texts">unequal</span>, we use Welch's t-test:
      </p>

      <p v-if="calculationData.sampleType === 'Dependent'" class="formulas">
        \[{{ currentStatistic[1] }}=\frac{\bar{d}-\mu_0}{ {{ currentStatistic[2] }}_d/\sqrt n}\]
      </p>

      <p v-if="calculationData.sampleType === 'Independent'" class="formulas">
        \[{{ currentStatistic[1] }}=\frac{(\bar{x}-\bar{y})-\mu_0}{\sqrt{\frac{ {{ currentStatistic[2] }}_x^2}{n_x}+\frac{ {{ currentStatistic[2] }}_y^2}{n_y}}}\]
      </p>

      <!-- 3 -->
      <li>Specify the level of significance (α):</li>
      <p class="formulas">
        \[\alpha=1-Confidence\ Level=1-{{ currentConfidenceLevel }}={{ calculationData.significanceLevel }}\]
      </p>

      <!-- 4 -->
      <li>Determine the critical values and state the decision rule:</li>

      <p v-if="currentTail === 'two-tailed'">
        To determine the critical values, we need to consult the <span class="bolded-texts">{{ currentTable }}</span>. Since this is a <span class="bolded-texts">two-sided test</span>, we divide the significance level (α) equally between the two tails.
      </p>
      <p v-else-if="currentTail === 'left-tailed'">
        To determine the critical values, we need to consult the <span class="bolded-texts">{{ currentTable }}</span>. Since this is a <span class="bolded-texts">one-sided left-tailed test</span>, we allocate the significance level (α) to the distribution’s left tail.
      </p>
      <p v-else-if="currentTail === 'right-tailed'">
        To determine the critical values, we need to consult the <span class="bolded-texts">{{ currentTable }}</span>. Since this is a <span class="bolded-texts">one-sided right-tailed test</span>, we allocate the significance level (α) to the distribution’s right tail.
      </p>

      <p v-if="currentTail === 'two-tailed'" class="small-margins">
        The critical value from the <span class="bolded-texts">{{ currentTable }}</span> for probability (p) = 1 – {{ calculationData.significanceLevel }}/2 <span v-if="calculationData.answer === 'No'">and ({{ degreesOfFreedomToSize }}-1) degrees of freedom</span> is:
      </p>
      <p v-else-if="['left-tailed', 'right-tailed'].includes(currentTail)" class="small-margins">
        The critical value from the <span class="bolded-texts">{{ currentTable }}</span> for probability (p) = 1 – {{ calculationData.significanceLevel }} <span v-if="calculationData.answer === 'No'">and ({{ degreesOfFreedomToSize }}-1) degrees of freedom</span> is:
      </p>
      <p v-if="calculationData.sampleType === 'Independent' && calculationData.answer === 'No' && calculationData.sampleAssumption === 'Unequal'" class="formulas">
        \[df = \dfrac{((s_x^2)/n_x + (s_y^2)/n_y)}{((s_x^2/n_x)^2/(n_x - 1) + (s_y^2/n_y)^2/(n_y - 1))} = \dfrac{(({{ calculationData.sdOne }}^2)/{{ calculationData.sizeOne }} + ({{ calculationData.sdTwo }}^2)/{{ calculationData.sizeTwo }})^2}{(({{ calculationData.sdOne }}^2)/{{ calculationData.sizeOne }})^2/({{ calculationData.sizeOne }} - 1) + (({{ calculationData.sdTwo }}^2)/{{ calculationData.sizeTwo }})^2/({{ calculationData.sizeTwo }} - 1)} = {{ calculationData.degreesOfFreedomOriginalValue }} \approx {{ calculationData.size }}\]
      </p>
      <p class="formulas">
        \[{{ currentStatistic[0] }}_{ {{ currentDegreesOfFreedom }}{{ significanceLevelDividedByTwo }} }={{ calculationData.currentScore }}\]
      </p>
      <p>The critical values for a <span class="bolded-texts">{{ currentTail === 'two-tailed' ? 'two sided' : currentTail }} test</span> are:</p>
      <p v-if="currentTail === 'two-tailed'" class="formulas">
        \[-{{ currentStatistic[0] }}_{ {{ currentDegreesOfFreedom }}{{ significanceLevelDividedByTwo }} }=-{{ calculationData.currentScore }}\ and\ {{ currentStatistic[0] }}_{ {{ currentDegreesOfFreedom }}{{ significanceLevelDividedByTwo }} }={{ calculationData.currentScore }}\]
      </p>
      <p v-else-if="currentTail === 'left-tailed'">
        \[-{{ currentStatistic[0] }}_{ {{ currentDegreesOfFreedom }}{{ significanceLevelDividedByTwo }} }=-{{ calculationData.currentScore }}\]
      </p>
      <p v-else>
        \[{{ currentStatistic[0] }}_{ {{ currentDegreesOfFreedom }}{{ significanceLevelDividedByTwo }} }={{ calculationData.currentScore }}\]
      </p>
      <p>The decision rule (based on the critical value approach) is:</p>
      <div v-if="currentTail === 'two-tailed'">
        <p class="formulas">
          \[If\ {{ currentStatistic[1] }}\ ≤ {{ currentStatistic[0] }}_{ {{ currentDegreesOfFreedom }}{{ significanceLevelDividedByTwo }} }\ or\ {{ currentStatistic[1] }}\ ≥ {{ currentStatistic[0] }}_{ {{ currentDegreesOfFreedom }}{{ significanceLevelDividedByTwo }} },\ reject\ H_0\]
        </p>
        <p class="formulas">
          \[If\ {{ currentStatistic[1] }}\ \gt {{ currentStatistic[0] }}_{ {{ currentDegreesOfFreedom }}{{ significanceLevelDividedByTwo }} }\ or\ {{ currentStatistic[1] }}\ \lt {{ currentStatistic[0] }}_{ {{ currentDegreesOfFreedom }}{{ significanceLevelDividedByTwo }} },\ fail\ to\ reject\ H_0\]
        </p>
      </div>
      <div v-else-if="currentTail === 'left-tailed'">
        <p class="formulas">
          \[If\ {{ currentStatistic[1] }} ≤ -{{ currentStatistic[0] }}_{ {{ calculationData.significanceLevel }} },\ reject\ H_0\]
        </p>
        <p class="formulas">
          \[If\ {{ currentStatistic[1] }} > -{{ currentStatistic[0] }}_{ {{ calculationData.significanceLevel }} },\ fail\ to\ reject\ H_0\]
        </p>
      </div>
      <div v-else>
        <p class="formulas">
          \[If\ {{ currentStatistic[1] }} ≥ {{ currentStatistic[0] }}_{ {{ calculationData.significanceLevel }} },\ reject\ H_0\]
        </p>
        <p class="formulas">
          \[If\ {{ currentStatistic[1] }} &lt; {{ currentStatistic[0] }}_{ {{ calculationData.significanceLevel }} },\ fail\ to\ reject\ H_0\]
        </p>
      </div>
      <p class="top-margin-mobile">
        The decision rule (based on the p-value approach) is:
      </p>
      <p class="formulas">
        \[p-value\ ≤\ {{ calculationData.significanceLevel }},\ Reject\ H_0\]
      </p>
      <p class="formulas">
        \[p-value\ \gt\ {{ calculationData.significanceLevel }},\ Fail\ to\ reject\ H_0\]
      </p>

      <!-- 5 -->
      <li>Calculate the test statistic:</li>
      <p v-if="calculationData.sampleType === 'Dependent'" class="formulas">
        \[{{ currentStatistic[1] }} = \dfrac{\overline{d} - \mu_0}{ {{ currentStatistic[2] }}_d/\sqrt{n} } = \dfrac{ {{ calculationData.mean }} - {{ calculationData.expectedDifference }} }{ {{ calculationData.standardDeviation }}/\sqrt{ {{ calculationData.size }} } } = {{ calculationData.testStatistic }}\]
      </p>
      <p v-if="calculationData.sampleType === 'Independent' && (calculationData.answer === 'Yes' || calculationData.sampleAssumption === 'Unequal')" class="formulas">
        \[{{ currentStatistic[1] }} = \dfrac{(\overline{x} - \overline{y}) - \mu_0}{\sqrt{\dfrac{ {{ currentStatistic[2] }}_x^2}{n_x} + \dfrac{ {{ currentStatistic[2] }}_y^2}{n_y}}} = \dfrac{({{ calculationData.meanBefore }} - {{ calculationData.meanAfter }}) - {{ calculationData.expectedDifference }} }{\sqrt{\dfrac{ {{ calculationData.sdOne }}^2}{ {{ calculationData.sizeOne }} } + \dfrac{ {{ calculationData.sdTwo }}^2}{ {{ calculationData.sizeTwo }} }}} = {{ calculationData.testStatistic }}\]
      </p>
      <p v-if="calculationData.sampleType === 'Independent' && calculationData.answer === 'No' && calculationData.sampleAssumption === 'Equal'" class="formulas">
        \[s_p^2 = \dfrac{(n_x - 1)s_x^2 + (n_y - 1)s_y^2}{n_x + n_y - 2} = \dfrac{( {{ calculationData.sizeOne }} - 1){{ calculationData.sdOne }}^2 + ({{ calculationData.sizeTwo }} - 1){{ calculationData.sdTwo }}^2}{ {{ calculationData.sizeOne }} + {{ calculationData.sizeTwo }} - 2} = {{ calculationData.pooledVariance }}\]
      </p>
      <p v-if="calculationData.sampleType === 'Independent' && calculationData.answer === 'No' && calculationData.sampleAssumption === 'Equal'" class="formulas">
        \[T = \dfrac{({{ calculationData.meanBefore }} - {{ calculationData.meanAfter }}) - {{ calculationData.expectedDifference }} }{\sqrt{\dfrac{ {{ calculationData.pooledVariance }} }{ {{ calculationData.sizeOne }} } + \dfrac{ {{ calculationData.pooledVariance }} }{ {{ calculationData.sizeTwo }} }}} = {{ calculationData.testStatistic }}\]
      </p>

      <!-- 6 -->
      <li>Make a decision:</li>
      <ul>
        <li>Critical Value Approach</li>
        <p v-if="currentTail === 'two-tailed'" class="formulas">
          \[If\ {{ calculationData.testStatistic }}\ ≤ -{{ calculationData.currentScore }}\ or\ {{ calculationData.testStatistic }}\ ≥\ {{ calculationData.currentScore }},\ then\ reject\ H_0\]
        </p>
        <p v-else-if="currentTail === 'left-tailed'" class="formulas">
          \[If\ {{ calculationData.testStatistic }}≤-{{ calculationData.currentScore }},\ then\ reject\ H_0\]
        </p>
        <p v-else class="formulas">
          \[If\ {{ calculationData.testStatistic }}≥{{ calculationData.currentScore }},\ then\ reject\ H_0\]
        </p>
        <p v-if="currentTail === 'two-tailed'">
          <template v-if="valuesApproach.critical[currentTail]">
            Conclusion: <span class="bolded-texts">Reject</span> <span class="inline-formulas">\[H_0\]</span> since the calculated <span class="bolded-texts">{{ currentStatistic[0] }}-statistic</span> falls outside the bounds of the two critical values.
          </template>
          <template v-else>
            Conclusion: <span class="bolded-texts">Fail to reject</span> <span class="inline-formulas">\[H_0\]</span> because the calculated <span class="bolded-texts">{{ currentStatistic[0] }}-statistic</span> falls between the two critical values.
          </template>
        </p>
        <p v-if="currentTail === 'left-tailed'">
          <template v-if="valuesApproach.critical[currentTail]">
            Conclusion: <span class="bolded-texts">Reject</span> <span class="inline-formulas">\[H_0\]</span> because the calculated <span class="bolded-texts">{{ currentStatistic[0] }}-statistic</span> is lower than the critical value.
          </template>
          <template v-else>
            Conclusion: <span class="bolded-texts">Fail to reject</span> <span class="inline-formulas">\[H_0\]</span> because the calculated <span class="bolded-texts">{{ currentStatistic[0] }}-statistic</span> is higher than the critical value.
          </template>
        </p>
        <p v-if="currentTail === 'right-tailed'">
          <template v-if="valuesApproach.critical[currentTail]">
            Conclusion: <span class="bolded-texts">Reject</span> <span class="inline-formulas">\[H_0\]</span> because the calculated <span class="bolded-texts">{{ currentStatistic[0] }}-statistic</span> is higher than the critical value.
          </template>
          <template v-else>
            Conclusion: <span class="bolded-texts">Fail to reject</span> <span class="inline-formulas">\[H_0\]</span> because the calculated <span class="bolded-texts">{{ currentStatistic[0] }}-statistic</span> is lower than the critical value.
          </template>
        </p>

        <li>P-Value Approach</li>
        <p class="formulas">
          \[p-value\ =\ {{ calculationData.pValue }}\]
        </p>
        <p class="formulas">
          \[p-value\ ≤\ {{ calculationData.significanceLevel }},\ Reject\ H_0\]
        </p>
        <p v-if="currentTail === 'two-tailed'">
          <template v-if="valuesApproach['p-value'][currentTail]">
            Conclusion: <span class="bolded-texts">Reject</span> <span class="inline-formulas">\[H_0\]</span> since the p-value is less than the specified significance level (α).
          </template>
          <template v-else>
            Conclusion: <span class="bolded-texts">Fail to reject</span> <span class="inline-formulas">\[H_0\]</span> because the p-value is higher than the specified significance level (α).
          </template>
        </p>
        <p v-if="currentTail === 'left-tailed'">
          <template v-if="valuesApproach['p-value'][currentTail]">
            Conclusion: <span class="bolded-texts">Reject</span> <span class="inline-formulas">\[H_0\]</span> because the p-value is lower than the specified significance level (α).
          </template>
          <template v-else>
            Conclusion: <span class="bolded-texts">Fail to reject</span> <span class="inline-formulas">\[H_0\]</span> because the p-value is higher than the specified significance level (α).
          </template>
        </p>
        <p v-if="currentTail === 'right-tailed'">
          <template v-if="valuesApproach['p-value'][currentTail]">
            Conclusion: <span class="bolded-texts">Reject</span> <span class="inline-formulas">\[H_0\]</span> since the p-value is less than the specified significance level (α).
          </template>
          <template v-else>
            Conclusion: <span class="bolded-texts">Fail to reject</span> <span class="inline-formulas">\[H_0\]</span> because the p-value is higher than the specified significance level (α).
          </template>
        </p>
      </ul>
    </ol>

    <!-- Python -->
    <div v-if="result" class="python-r-codes python">
      <span class="img-text"><img alt="Python icon" src="../../../../assets/images/icon-python-calc.png"> <h4 class="h6">Your Result in Python:</h4></span>
      <p class="codes-info">
        The following Python code should provide the hypothesis testing using the input data you entered.
      </p>
      <div class="codes-container">
        <div class="codes">
          <div v-if="calculationData.sampleType === 'Dependent'">
            <!-- Yes --------------------------------------------------------------------------------------------------- -->

            <div v-if="calculationData.answer === 'Yes'">
              <!-- two tails -->
              <pre
                v-if="calculationData.method === 'readyData' && currentTail === 'two-tailed'"
                class="language-python"
              ><code id="python">
import numpy as np

from scipy.stats import norm

# Sample size

n = {{ calculationData.size }}

# Known standard deviation

std_dev = {{ calculationData.standardDeviation }}

# Calculate the sample mean of the differences

mean_diff = {{ calculationData.mean }}

# Calculate the standard error of the mean difference

se = std_dev / np.sqrt(n)

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is not zero (two-tailed test)
null_mean = {{ calculationData.expectedDifference }}

# Calculate the z-statistic

z_statistic = (mean_diff - null_mean) / se

# Calculate the critical values at a given alpha level (two-tailed test)

alpha = {{ calculationData.significanceLevel }}

critical_value = norm.ppf(1 - alpha / 2)

# Calculate the p-value

p_value = 2 * (1 - norm.cdf(abs(z_statistic)))

# Print the results

print("Sample Mean Difference:", mean_diff)

print("Standard Error:", se)

print("Z-Statistic:", z_statistic)

print("P-Value:", p_value)

print("Critical Values:", -critical_value, critical_value)

# Make a decision based on the p-value and critical values

if p_value &lt; alpha:

    print("Reject the null hypothesis. There is a significant difference in means.")

else:

    print("Fail to reject the null hypothesis. There is no significant difference in means.")
                </code></pre>
              <pre
                v-if="calculationData.method === 'rawData' && currentTail === 'two-tailed'"
                class="language-python"
              ><code id="python">
import numpy as np

from scipy.stats import norm

# Paired data

before = <span class="no-wrap">{{ inputToNumbers[0][0] }}</span>

after = <span class="no-wrap">{{ inputToNumbers[0][1] }}</span>

# Calculate the differences between paired observations

differences = np.array(after) - np.array(before)

# Sample size

n = len(differences)

# Known standard deviation

std_dev = {{ calculationData.standardDeviation }}

# Calculate the sample mean of the differences

mean_diff = np.mean(differences)

# Calculate the standard error of the mean difference

se = std_dev / np.sqrt(n)

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is not zero (two-tailed test)

null_mean = {{ calculationData.expectedDifference }}

# Calculate the z-statistic

z_statistic = (mean_diff - null_mean) / se

# Calculate the critical values at a given alpha level (two-tailed test)

alpha = {{ calculationData.significanceLevel }}

critical_value = norm.ppf(1 - alpha / 2)

# Calculate the p-value

p_value = 2 * (1 - norm.cdf(abs(z_statistic)))

# Print the results

print("Sample Mean Difference:", mean_diff)

print("Standard Error:", se)

print("Z-Statistic:", z_statistic)

print("P-Value:", p_value)

print("Critical Values:", -critical_value, critical_value)

# Make a decision based on the p-value and critical values

if p_value &lt; alpha:

    print("Reject the null hypothesis. There is a significant difference in means.")

else:

    print("Fail to reject the null hypothesis. There is no significant difference in means.")</code></pre>

              <!-- left tail -->
              <pre
                v-if="calculationData.method === 'readyData' && currentTail === 'left-tailed'"
                class="language-python"
              ><code id="python">
import numpy as np

from scipy.stats import norm

# Sample size

n = {{ calculationData.size }}

# Known standard deviation

std_dev = {{ calculationData.standardDeviation }}

# Calculate the sample mean of the differences

mean_diff = {{ calculationData.mean }}

# Calculate the standard error of the mean difference

se = std_dev / np.sqrt(n)

# Null hypothesis: The mean difference is zero or greater

# Alternative hypothesis: The mean difference is less than zero (left-tailed test)

null_mean = {{ calculationData.expectedDifference }}

# Calculate the z-statistic

z_statistic = (mean_diff - null_mean) / se

# Calculate the critical value at a given alpha level (left-tailed test)

alpha = {{ calculationData.significanceLevel }}

critical_value = norm.ppf(alpha)

# Calculate the p-value

p_value = norm.cdf(z_statistic)

# Print the results

print("Sample Mean Difference:", mean_diff)

print("Standard Error:", se)

print("Z-Statistic:", z_statistic)

print("P-Value:", p_value)

print("Critical Value:", critical_value)

# Make a decision based on the p-value and critical value

if p_value &lt; alpha:

    print("Reject the null hypothesis. There is a significant decrease in means.")

else:

    print("Fail to reject the null hypothesis. There is no significant decrease in means.")</code>

              </pre>
              <pre
                v-if="calculationData.method === 'rawData' && currentTail === 'left-tailed'"
                class="language-python"
              ><code id="python">
import numpy as np

from scipy.stats import norm

# Paired data

before = <span class="no-wrap">{{ inputToNumbers[0][0] }}</span>

after = <span class="no-wrap">{{ inputToNumbers[0][1] }}</span>

# Calculate the differences between paired observations

differences = np.array(after) - np.array(before)

# Sample size

n = len(differences)

# Known standard deviation

std_dev = {{ calculationData.standardDeviation }}

# Calculate the sample mean of the differences

mean_diff = np.mean(differences)

# Calculate the standard error of the mean difference

se = std_dev / np.sqrt(n)

# Null hypothesis: The mean difference is zero or greater

# Alternative hypothesis: The mean difference is less than zero (left-tailed test)

null_mean = {{ calculationData.expectedDifference }}

# Calculate the z-statistic

z_statistic = (mean_diff - null_mean) / se

# Calculate the critical value at a given alpha level (left-tailed test)

alpha = {{ calculationData.significanceLevel }}

critical_value = norm.ppf(alpha)

# Calculate the p-value

p_value = norm.cdf(z_statistic)

# Print the results

print("Sample Mean Difference:", mean_diff)

print("Standard Error:", se)

print("Z-Statistic:", z_statistic)

print("P-Value:", p_value)

print("Critical Value:", critical_value)

# Make a decision based on the p-value and critical value

if p_value &lt; alpha:

    print("Reject the null hypothesis. There is a significant decrease in means.")

else:

    print("Fail to reject the null hypothesis. There is no significant decrease in means.")</code></pre>

              <!-- right tail -->
              <pre
                v-if="calculationData.method === 'readyData' && currentTail === 'right-tailed'"
                class="language-python"
              ><code id="python">
import numpy as np

from scipy.stats import norm

# Sample size

n = {{ calculationData.size }}

# Known standard deviation

std_dev = {{ calculationData.standardDeviation }}

# Calculate the sample mean of the differences

mean_diff = {{ calculationData.mean }}

# Calculate the standard error of the mean difference

se = std_dev / np.sqrt(n)

# Null hypothesis: The mean difference is zero or less

# Alternative hypothesis: The mean difference is greater than zero (right-tailed test)

null_mean = {{ calculationData.expectedDifference }}

# Calculate the z-statistic

z_statistic = (mean_diff - null_mean) / se

# Calculate the critical value at a given alpha level (right-tailed test)

alpha = {{ calculationData.significanceLevel }}

critical_value = norm.ppf(1 - alpha)

# Calculate the p-value

p_value = 1 - norm.cdf(z_statistic)

# Print the results

print("Sample Mean Difference:", mean_diff)

print("Standard Error:", se)

print("Z-Statistic:", z_statistic)

print("P-Value:", p_value)

print("Critical Value:", critical_value)

# Make a decision based on the p-value and critical value

if p_value &lt; alpha:

    print("Reject the null hypothesis. There is evidence of a significant increase.")

else:

    print("Fail to reject the null hypothesis. There is no evidence of a significant increase.")</code></pre>
              <pre
                v-if="calculationData.method === 'rawData' && currentTail === 'right-tailed'"
                class="language-python"
              ><code id="python">
import numpy as np

from scipy.stats import norm

# Paired data

before = <span class="no-wrap">{{ inputToNumbers[0][0] }}</span>

after = <span class="no-wrap">{{ inputToNumbers[0][1] }}</span>

# Calculate the differences between paired observations

differences = np.array(after) - np.array(before)

# Sample size

n = len(differences)

# Known standard deviation

std_dev = {{ calculationData.standardDeviation }}

# Calculate the sample mean of the differences

mean_diff = np.mean(differences)

# Calculate the standard error of the mean difference

se = std_dev / np.sqrt(n)

# Null hypothesis: The mean difference is zero or less

# Alternative hypothesis: The mean difference is greater than zero (right-tailed test)

null_mean = {{ calculationData.expectedDifference }}

# Calculate the z-statistic

z_statistic = (mean_diff - null_mean) / se

# Calculate the critical value at a given alpha level (right-tailed test)

alpha = {{ calculationData.significanceLevel }}

critical_value = norm.ppf(1 - alpha)

# Calculate the p-value

p_value = 1 - norm.cdf(z_statistic)

# Print the results

print("Sample Mean Difference:", mean_diff)

print("Standard Error:", se)

print("Z-Statistic:", z_statistic)

print("P-Value:", p_value)

print("Critical Value:", critical_value)

# Make a decision based on the p-value and critical value

if p_value &lt; alpha:

    print("Reject the null hypothesis. There is evidence of a significant increase.")

else:

    print("Fail to reject the null hypothesis. There is no evidence of a significant increase.")</code></pre>
            </div>

            <!-- No ------------------------------------------------------------------------------------------------------- -->

            <div v-if="calculationData.answer === 'No'">
              <!-- two tails -->
              <pre
                v-if="calculationData.method === 'readyData' && currentTail === 'two-tailed'"
                class="language-python"
              ><code id="python">
import numpy as np

from scipy import stats

# Given data

sample_mean_diff = {{ calculationData.mean }}

sample_std_dev = {{ calculationData.standardDeviation }}

n = {{ calculationData.size }}

# Null hypothesis: The mean difference is zero

null_mean = {{ calculationData.expectedDifference }}

# Compute the t-statistic

t_statistic = (sample_mean_diff - null_mean) / (sample_std_dev / np.sqrt(n))

# Compute the p-value for the t-statistic with n-1 degrees of freedom

p_value = stats.t.sf(np.abs(t_statistic), df=n-1) * 2  # two-tailed test

# Calculate the critical values at a given alpha level (two-tailed test)

alpha = {{ calculationData.significanceLevel }}

critical_value_left = stats.t.ppf(alpha / 2, df=n-1)

critical_value_right = stats.t.ppf(1 - alpha / 2, df=n-1)

# Print the results

print("Sample Mean Difference:", sample_mean_diff)

print("T-Statistic:", t_statistic)

print("P-Value:", p_value)

print("Critical Values:", critical_value_left, critical_value_right)

# Make a decision based on the p-value and critical values

if p_value &lt; alpha:

    print("Reject the null hypothesis. There is evidence of a significant difference.")

else:

    print("Fail to reject the null hypothesis. There is no evidence of a significant difference.")</code>
              </pre>
              <pre
                v-if="calculationData.method === 'rawData' && currentTail === 'two-tailed'"
                class="language-python"
              ><code id="python">
import numpy as np

from scipy import stats

# Paired data

before = <span class="no-wrap">{{ inputToNumbers[0][0] }}</span>

after = <span class="no-wrap">{{ inputToNumbers[0][1] }}</span>

# Calculate the differences between paired observations

differences = np.array(after) - np.array(before)

# Sample size

n = len(differences)

# Null hypothesis: The mean difference is zero

null_mean = {{ calculationData.expectedDifference }}

# Perform the paired t-test

t_statistic, p_value = stats.ttest_1samp(differences, null_mean)

# Calculate the critical values at a given alpha level (two-tailed test)

alpha = {{ calculationData.significanceLevel }}

critical_value_left = stats.t.ppf(alpha / 2, df=n-1)

critical_value_right = stats.t.ppf(1 - alpha / 2, df=n-1)

# Print the results

print("Sample Mean Difference:", differences.mean())

print("T-Statistic:", t_statistic)

print("P-Value:", p_value)

print("Critical Values:", critical_value_left, critical_value_right)

# Make a decision based on the p-value and critical values

if p_value &lt; alpha:

    print("Reject the null hypothesis. There is evidence of a significant difference.")

else:

    print("Fail to reject the null hypothesis. There is no evidence of a significant difference.")
</code>
              </pre>

              <!-- left tail -->
              <pre
                v-if="calculationData.method === 'readyData' && currentTail === 'left-tailed'"
                class="language-python"
              ><code id="python">
import numpy as np

from scipy import stats

# Given data

sample_mean_diff = {{ calculationData.mean }}

sample_std_dev = {{ calculationData.standardDeviation }}

n = {{ calculationData.size }}

# Null hypothesis: The mean difference is zero

null_mean = {{ calculationData.expectedDifference }}

# Compute the t-statistic using the provided sample mean difference and standard deviation

t_statistic = (sample_mean_diff - null_mean) / (sample_std_dev / np.sqrt(n))

# Compute the p-value for the t-statistic with n-1 degrees of freedom (for left-tailed test)

two_tailed_p_value = stats.t.sf(np.abs(t_statistic), df=n-1) * 2  # two-tailed test

# Adjust the p-value for a left-tailed test

if t_statistic &lt; 0:

    left_tailed_p_value = two_tailed_p_value / 2

else:

    left_tailed_p_value = 1 - two_tailed_p_value / 2

# Calculate the critical value for the given alpha level (left-tailed test)

alpha = {{ calculationData.significanceLevel }}

critical_value = stats.t.ppf(alpha, df=n-1)

# Print the results

print("Sample Mean Difference:", sample_mean_diff)

print("T-Statistic:", t_statistic)

print("P-Value:", left_tailed_p_value)

print("Critical Value:", critical_value)

# Make a decision based on the p-value and critical value

if left_tailed_p_value &lt; alpha and t_statistic &lt; 0:

    print("Reject the null hypothesis. There is evidence of a significant decrease.")

else:

    print("Fail to reject the null hypothesis. There is no evidence of a significant decrease.")
  </code></pre>
              <pre
                v-if="calculationData.method === 'rawData' && currentTail === 'left-tailed'"
                class="language-python"
              ><code id="python">
import numpy as np

from scipy import stats

# Paired data

before = <span class="no-wrap">{{ inputToNumbers[0][0] }}</span>

after = <span class="no-wrap">{{ inputToNumbers[0][1] }}</span>

# Calculate the differences between paired observations

differences = np.array(after) - np.array(before)

# Sample size

n = len(differences)

# Null hypothesis: The mean difference is zero

null_mean = {{ calculationData.expectedDifference }}

# Perform the paired t-test

t_statistic, two_tailed_p_value = stats.ttest_1samp(differences, null_mean)

# Adjust the p-value for a left-tailed test

if t_statistic &lt; 0:

    left_tailed_p_value = two_tailed_p_value / 2

else:

    left_tailed_p_value = 1 - two_tailed_p_value / 2

# Calculate the critical value for the given alpha level (left-tailed test)

alpha = {{ calculationData.significanceLevel }}

critical_value = stats.t.ppf(alpha, df=n-1)

# Print the results

print("Sample Mean Difference:", differences.mean())

print("T-Statistic:", t_statistic)

print("P-Value:", left_tailed_p_value)

print("Critical Value:", critical_value)

# Make a decision based on the p-value and critical value

if left_tailed_p_value &lt; alpha and t_statistic &lt; 0:

    print("Reject the null hypothesis. There is evidence of a significant decrease.")

else:

    print("Fail to reject the null hypothesis. There is no evidence of a significant decrease.")
</code></pre>

              <!-- right tail -->
              <pre
                v-if="calculationData.method === 'readyData' && currentTail === 'right-tailed'"
                class="language-python"
              ><code id="python">
import numpy as np

from scipy import stats

# Given data

sample_mean_diff = {{ calculationData.mean }}

sample_std_dev = {{ calculationData.standardDeviation }}

n = {{ calculationData.size }}

# Null hypothesis: The mean difference is zero

null_mean = {{ calculationData.expectedDifference }}

# Compute the t-statistic using the provided sample mean difference and standard deviation

t_statistic = (sample_mean_diff - null_mean) / (sample_std_dev / np.sqrt(n))

# Compute the p-value for the t-statistic with n-1 degrees of freedom (for right-tailed test)

two_tailed_p_value = stats.t.sf(np.abs(t_statistic), df=n-1) * 2  # two-tailed test

# Adjust the p-value for a right-tailed test

if t_statistic > 0:

    right_tailed_p_value = two_tailed_p_value / 2

else:

    right_tailed_p_value = 1 - two_tailed_p_value / 2

# Calculate the critical value for the given alpha level (right-tailed test)

alpha = {{ calculationData.significanceLevel }}

critical_value = stats.t.isf(alpha, df=n-1)  # Note: 'isf' is used for the right-tailed critical value

# Print the results

print("Sample Mean Difference:", sample_mean_diff)

print("T-Statistic:", t_statistic)

print("P-Value:", right_tailed_p_value)

print("Critical Value:", critical_value)

# Make a decision based on the p-value and critical value

if right_tailed_p_value &lt; alpha and t_statistic > critical_value:

    print("Reject the null hypothesis. There is evidence of a significant increase.")

else:

    print("Fail to reject the null hypothesis. There is no evidence of a significant increase.")</code></pre>
              <pre
                v-if="calculationData.method === 'rawData' && currentTail === 'right-tailed'"
                class="language-python"
              ><code id="python">
import numpy as np

from scipy import stats

# Paired data

before = <span class="no-wrap">{{ inputToNumbers[0][0] }}</span>

after = <span class="no-wrap">{{ inputToNumbers[0][1] }}</span>

# Calculate the differences between paired observations

differences = np.array(after) - np.array(before)

# Perform the paired t-test

t_statistic, p_value = stats.ttest_1samp(differences, null_mean)

# Adjust the p-value for a right-tailed test

if t_statistic > 0:

    right_tailed_p_value = p_value / 2

else:

    right_tailed_p_value = 1 - p_value / 2

# Calculate the critical value for the given alpha level (right-tailed test)

alpha = {{ calculationData.significanceLevel }}

critical_value_right_tailed = stats.t.ppf(1 - alpha, df=n-1)

# Print the results

print("Sample Mean Difference:", differences.mean())

print("T-Statistic:", t_statistic)

print("P-Value:", right_tailed_p_value)

print("Critical Value:", critical_value_right_tailed)

# Make a decision based on the p-value and critical value

if right_tailed_p_value &lt; alpha and t_statistic > 0:

    print("Reject the null hypothesis. There is evidence of a significant increase.")

else:

    print("Fail to reject the null hypothesis. There is no evidence of a significant increase.")
</code></pre>
            </div>
          </div>

          <!-- ------------------------------------------------------------------------------------------------------------------------------------------------------------ -->

          <div v-if="calculationData.sampleType === 'Independent'">
            <!-- Yes --------------------------------------------------------------------------------------------------- -->

            <div v-if="calculationData.answer === 'Yes'">
              <!-- two tails -->
              <pre
                v-if="calculationData.method === 'readyData' && currentTail === 'two-tailed'"
                class="language-python"
              ><code id="python">
import numpy as np

from scipy.stats import norm

# Population standard deviations

std_dev1 = {{ calculationData.sdOne }}

std_dev2 = {{ calculationData.sdTwo }}

# Sample sizes

n1 = {{ calculationData.sizeOne }}

n2 = {{ calculationData.sizeTwo }}

# Calculate the mean difference

mean_diff = {{ calculationData.meanBefore }} - {{ calculationData.meanAfter }}

# Calculate the standard error of the difference

se = np.sqrt((std_dev1**2/n1) + (std_dev2**2/n2))

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is not zero

null_mean = {{ calculationData.expectedDifference }}

# Calculate the z-statistic

z_statistic = (mean_diff - null_mean) / se

# Calculate the two-tailed p-value

p_value = 2 * (1 - norm.cdf(abs(z_statistic)))

# Calculate the critical value for alpha = {{ calculationData.significanceLevel }}

alpha = {{ calculationData.significanceLevel }}

critical_value = norm.ppf(1 - alpha/2)

# Print the results

print("Mean Difference:", mean_diff)

print("Standard Error:", se)

print("Z-Statistic:", z_statistic)

print("P-Value:", p_value)

print("Critical Value:", critical_value)

# Make a decision based on the z-statistic and critical value

if abs(z_statistic) > critical_value:

    print("Reject the null hypothesis. There is evidence of a significant difference between the two samples.")

else:

    print("Fail to reject the null hypothesis. There is no evidence of a significant difference between the two samples.")
</code></pre>
              <pre
                v-if="calculationData.method === 'rawData' && currentTail === 'two-tailed'"
                class="language-python"
              ><code id="python">
import numpy as np

from scipy.stats import norm

# Sample data

sample1 = <span class="no-wrap">{{ inputToNumbers[0][0] }}</span>

sample2 = <span class="no-wrap">{{ inputToNumbers[0][1] }}</span>

# Population standard deviations

std_dev1 = {{ calculationData.sdOne }}

std_dev2 = {{ calculationData.sdTwo }}

# Sample sizes

n1 = len(sample1)

n2 = len(sample2)

# Calculate the mean difference

mean_diff = np.mean(sample1) - np.mean(sample2)

# Calculate the standard error of the difference

se = np.sqrt((std_dev1**2/n1) + (std_dev2**2/n2))

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is not zero

null_mean = {{ calculationData.expectedDifference }}

# Calculate the z-statistic

z_statistic = (mean_diff - null_mean) / se

# Calculate the two-tailed p-value

p_value = 2 * (1 - norm.cdf(abs(z_statistic)))

# Calculate the critical value for alpha = {{ calculationData.significanceLevel }}

alpha = {{ calculationData.significanceLevel }}

critical_value = norm.ppf(1 - alpha/2)

# Print the results

print("Mean Difference:", mean_diff)

print("Standard Error:", se)

print("Z-Statistic:", z_statistic)

print("P-Value:", p_value)

print("Critical Value:", critical_value)

# Make a decision based on the z-statistic and critical value

if abs(z_statistic) > critical_value:

    print("Reject the null hypothesis. There is evidence of a significant difference between the two samples.")

else:

    print("Fail to reject the null hypothesis. There is no evidence of a significant difference between the two samples.")
  </code></pre>

              <!-- left tail -->
              <pre
                v-if="calculationData.method === 'readyData' && currentTail === 'left-tailed'"
                class="language-python"
              ><code id="python">
import numpy as np

from scipy.stats import norm

# Population standard deviations

std_dev1 = {{ calculationData.sdOne }}

std_dev2 = {{ calculationData.sdTwo }}

# Sample sizes

n1 = {{ calculationData.sizeOne }}

n2 = {{ calculationData.sizeTwo }}

# Calculate the mean difference

mean_diff = {{ calculationData.meanBefore }} - {{ calculationData.meanAfter }}

# Calculate the standard error of the difference

se = np.sqrt((std_dev1**2/n1) + (std_dev2**2/n2))

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is less than zero

null_mean = {{ calculationData.expectedDifference }}

# Calculate the z-statistic

z_statistic = (mean_diff - null_mean) / se

# Calculate the p-value for a left-tailed test

p_value = norm.cdf(z_statistic)

# Calculate the critical value for alpha = {{ calculationData.significanceLevel }} (left-tailed test)

alpha = {{ calculationData.significanceLevel }}

critical_value = norm.ppf(alpha)

# Print the results

print("Mean Difference:", mean_diff)

print("Standard Error:", se)

print("Z-Statistic:", z_statistic)

print("P-Value:", p_value)

print("Critical Value:", critical_value)

# Make a decision based on the z-statistic and critical value

if z_statistic &lt; critical_value:

    print("Reject the null hypothesis. There is evidence of a significant decrease in the second sample compared to the first sample.")

else:

    print("Fail to reject the null hypothesis. There is no evidence of a significant decrease in the second sample compared to the first sample.")

    </code></pre>
              <pre
                v-if="calculationData.method === 'rawData' && currentTail === 'left-tailed'"
                class="language-python"
              ><code id="python">
import numpy as np

from scipy.stats import norm

# Sample data

sample1 = <span class="no-wrap">{{ inputToNumbers[0][0] }}</span>

sample2 = <span class="no-wrap">{{ inputToNumbers[0][1] }}</span>

# Population standard deviations

std_dev1 = {{ calculationData.sdOne }}

std_dev2 = {{ calculationData.sdTwo }}

# Sample sizes

n1 = len(sample1)

n2 = len(sample2)

# Calculate the mean difference

mean_diff = np.mean(sample1) - np.mean(sample2)

# Calculate the standard error of the difference

se = np.sqrt((std_dev1**2/n1) + (std_dev2**2/n2))

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is less than zero

null_mean = {{ calculationData.expectedDifference }}

# Calculate the z-statistic

z_statistic = (mean_diff - null_mean) / se

# Calculate the p-value for a left-tailed test

p_value = norm.cdf(z_statistic)

# Calculate the critical value for alpha = {{ calculationData.significanceLevel }} (left-tailed test)

alpha = {{ calculationData.significanceLevel }}

critical_value = norm.ppf(alpha)

# Print the results

print("Mean Difference:", mean_diff)

print("Standard Error:", se)

print("Z-Statistic:", z_statistic)

print("P-Value:", p_value)

print("Critical Value:", critical_value)

# Make a decision based on the z-statistic and critical value

if z_statistic &lt; critical_value:

    print("Reject the null hypothesis. There is evidence of a significant decrease in the second sample compared to the first sample.")

else:

    print("Fail to reject the null hypothesis. There is no evidence of a significant decrease in the second sample compared to the first sample.")</code></pre>

              <!-- right tail -->
              <pre
                v-if="calculationData.method === 'readyData' && currentTail === 'right-tailed'"
                class="language-python"
              ><code id="python">
import numpy as np

from scipy.stats import norm

# Population standard deviations

std_dev1 = {{ calculationData.sdOne }}

std_dev2 = {{ calculationData.sdTwo }}

# Sample sizes

n1 = {{ calculationData.sizeOne }}

n2 = {{ calculationData.sizeTwo }}

# Calculate the mean difference

mean_diff = {{ calculationData.meanBefore }} - {{ calculationData.meanAfter }}

# Calculate the standard error of the difference

se = np.sqrt((std_dev1**2/n1) + (std_dev2**2/n2))

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is greater than zero

null_mean = {{ calculationData.expectedDifference }}

# Calculate the z-statistic

z_statistic = (mean_diff - null_mean) / se

# Calculate the p-value for a right-tailed test

p_value = 1 - norm.cdf(z_statistic)

# Calculate the critical value for alpha = {{ calculationData.significanceLevel }} (right-tailed test)

alpha = {{ calculationData.significanceLevel }}

critical_value = norm.ppf(1 - alpha)

# Print the results

print("Mean Difference:", mean_diff)

print("Standard Error:", se)

print("Z-Statistic:", z_statistic)

print("P-Value:", p_value)

print("Critical Value:", critical_value)

# Make a decision based on the z-statistic and critical value

if z_statistic > critical_value:

    print("Reject the null hypothesis. There is evidence of a significant increase in the first sample compared to the second sample.")

else:

    print("Fail to reject the null hypothesis. There is no evidence of a significant increase in the first sample compared to the second sample.")
</code></pre>
              <pre
                v-if="calculationData.method === 'rawData' && currentTail === 'right-tailed'"
                class="language-python"
              ><code id="python">
import numpy as np

from scipy.stats import norm

# Sample data

sample1 = <span class="no-wrap">{{ inputToNumbers[0][0] }}</span>

sample2 = <span class="no-wrap">{{ inputToNumbers[0][1] }}</span>

# Population standard deviations

std_dev1 = {{ calculationData.sdOne }}

std_dev2 = {{ calculationData.sdTwo }}

# Sample sizes

n1 = len(sample1)

n2 = len(sample2)

# Calculate the mean difference

mean_diff = np.mean(sample1) - np.mean(sample2)

# Calculate the standard error of the difference

se = np.sqrt((std_dev1**2/n1) + (std_dev2**2/n2))

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is greater than zero

null_mean = {{ calculationData.expectedDifference }}

# Calculate the z-statistic

z_statistic = (mean_diff - null_mean) / se

# Calculate the p-value for a right-tailed test

p_value = 1 - norm.cdf(z_statistic)

# Calculate the critical value for alpha = {{ calculationData.significanceLevel }} (right-tailed test)

alpha = {{ calculationData.significanceLevel }}

critical_value = norm.ppf(1 - alpha)

# Print the results

print("Mean Difference:", mean_diff)

print("Standard Error:", se)

print("Z-Statistic:", z_statistic)

print("P-Value:", p_value)

print("Critical Value:", critical_value)

# Make a decision based on the z-statistic and critical value

if z_statistic > critical_value:

    print("Reject the null hypothesis. There is evidence of a significant increase in the first sample compared to the second sample.")

else:

    print("Fail to reject the null hypothesis. There is no evidence of a significant increase in the first sample compared to the second sample.")
</code></pre>
            </div>

            <!-- No ------------------------------------------------------------------------------------------------------- -->

            <!-- Equal -->
            <div v-if="calculationData.answer === 'No' && calculationData.sampleAssumption === 'Equal'">
              <!-- two tails -->
              <pre
                v-if="calculationData.method === 'readyData' && currentTail === 'two-tailed'"
                class="language-python"
              ><code id="python">
import numpy as np

from scipy.stats import t

# Calculate the sample means

mean1 = {{ calculationData.meanBefore }}

mean2 = {{ calculationData.meanAfter }}

# Calculate the sample standard deviations

std_dev1 = {{ calculationData.sdOne }}

std_dev2 = {{ calculationData.sdTwo }}

# Sample sizes

n1 = {{ calculationData.sizeOne }}

n2 = {{ calculationData.sizeTwo }}

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is not zero

null_mean = {{ calculationData.expectedDifference }}

# Calculate the pooled standard deviation

pooled_std_dev = np.sqrt(((n1-1) * std_dev1**2 + (n2-1) * std_dev2**2) / (n1 + n2 - 2))

# Calculate the standard error of the difference

se = pooled_std_dev * np.sqrt((1/n1) + (1/n2))

# Calculate the t-statistic

t_statistic = (mean1 - mean2 - null_mean) / se

# Degrees of freedom

df = n1 + n2 - 2

# Calculate the critical values at a given alpha level (two-tailed test)

alpha = {{ calculationData.significanceLevel }}

critical_value_left = t.ppf(alpha / 2, df=df)

critical_value_right = t.ppf(1 - alpha / 2, df=df)

# Calculate the p-value

p_value = 2 * (1 - t.cdf(abs(t_statistic), df=df))

# Print the results

print("Mean Difference:", mean1 - mean2)

print("Standard Error:", se)

print("T-Statistic:", t_statistic)

print("P-Value:", p_value)

print("Critical Values:", critical_value_left, critical_value_right)

# Make a decision based on the p-value and critical values

if p_value &lt; alpha:

    print("Reject the null hypothesis. There is evidence of a significant difference between the two samples.")

else:

    print("Fail to reject the null hypothesis. There is no evidence of a significant difference between the two samples.")</code></pre>
              <pre
                v-if="calculationData.method === 'rawData' && currentTail === 'two-tailed'"
                class="language-python"
              ><code id="python">
import numpy as np

from scipy.stats import t

# Sample 1

sample1 = <span class="no-wrap">{{ inputToNumbers[0][0] }}</span>

# Sample 2

sample2 = <span class="no-wrap">{{ inputToNumbers[0][1] }}</span>

# Calculate the sample means

mean1 = np.mean(sample1)

mean2 = np.mean(sample2)

# Calculate the sample standard deviations

std_dev1 = np.std(sample1, ddof=1)

std_dev2 = np.std(sample2, ddof=1)

# Sample sizes

n1 = len(sample1)

n2 = len(sample2)

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is not zero

null_mean = {{ calculationData.expectedDifference }}

# Calculate the pooled standard deviation

pooled_std_dev = np.sqrt(((n1-1) * std_dev1**2 + (n2-1) * std_dev2**2) / (n1 + n2 - 2))

# Calculate the standard error of the difference

se = pooled_std_dev * np.sqrt((1/n1) + (1/n2))

# Calculate the t-statistic

t_statistic = (mean1 - mean2 - null_mean) / se

# Degrees of freedom

df = n1 + n2 - 2

# Calculate the critical values at a given alpha level (two-tailed test)

alpha = {{ calculationData.significanceLevel }}

critical_value_left = t.ppf(alpha / 2, df=df)

critical_value_right = t.ppf(1 - alpha / 2, df=df)

# Calculate the p-value

p_value = 2 * (1 - t.cdf(abs(t_statistic), df=df))

# Print the results

print("Mean Difference:", mean1 - mean2)

print("Standard Error:", se)

print("T-Statistic:", t_statistic)

print("P-Value:", p_value)

print("Critical Values:", critical_value_left, critical_value_right)

# Make a decision based on the p-value and critical values

if p_value &lt; alpha:

    print("Reject the null hypothesis. There is evidence of a significant difference between the two samples.")

else:

    print("Fail to reject the null hypothesis. There is no evidence of a significant difference between the two samples.")</code>
              </pre>

              <!-- left tail -->
              <pre
                v-if="calculationData.method === 'readyData' && currentTail === 'left-tailed'"
                class="language-python"
              ><code id="python">
import numpy as np

from scipy.stats import t

# Calculate the sample means

mean1 = {{ calculationData.meanBefore }}

mean2 = {{ calculationData.meanAfter }}

# Calculate the sample standard deviations

std_dev1 = {{ calculationData.sdOne }}

std_dev2 = {{ calculationData.sdTwo }}

# Sample sizes

n1 = {{ calculationData.sizeOne }}

n2 = {{ calculationData.sizeTwo }}

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is less than zero

null_mean = {{ calculationData.expectedDifference }}

# Calculate the pooled standard deviation

pooled_std_dev = np.sqrt(((n1-1) * std_dev1**2 + (n2-1) * std_dev2**2) / (n1 + n2 - 2))

# Calculate the standard error of the difference

se = pooled_std_dev * np.sqrt((1/n1) + (1/n2))

# Calculate the t-statistic

t_statistic = (mean1 - mean2 - null_mean) / se

# Degrees of freedom

df = n1 + n2 - 2

# Calculate the critical value at a given alpha level (left-tailed test)

alpha = {{ calculationData.significanceLevel }}

critical_value = t.ppf(alpha, df=df)

# Calculate the p-value

p_value = t.cdf(t_statistic, df=df)

# Print the results

print("Mean Difference:", mean1 - mean2)

print("Standard Error:", se)

print("T-Statistic:", t_statistic)

print("P-Value:", p_value)

print("Critical Value:", critical_value)

# Make a decision based on the p-value and critical value

if p_value &lt; alpha and t_statistic &lt; 0:

    print("Reject the null hypothesis. There is evidence of a significant difference between the two samples, with Sample 1 mean being less than Sample 2 mean.")

else:

    print("Fail to reject the null hypothesis. There is no evidence of a significant difference between the two samples or the Sample 1 mean is not significantly less than the Sample 2 mean.")</code></pre>
              <pre
                v-if="calculationData.method === 'rawData' && currentTail === 'left-tailed'"
                class="language-python"
              ><code id="python">
import numpy as np

from scipy.stats import t

# Sample 1

sample1 = <span class="no-wrap">{{ inputToNumbers[0][0] }}</span>

# Sample 2

sample2 = <span class="no-wrap">{{ inputToNumbers[0][1] }}</span>

# Calculate the sample means

mean1 = np.mean(sample1)

mean2 = np.mean(sample2)

# Calculate the sample standard deviations

std_dev1 = np.std(sample1, ddof=1)

std_dev2 = np.std(sample2, ddof=1)

# Sample sizes

n1 = len(sample1)

n2 = len(sample2)

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is less than zero

null_mean = {{ calculationData.expectedDifference }}

# Calculate the pooled standard deviation

pooled_std_dev = np.sqrt(((n1-1) * std_dev1**2 + (n2-1) * std_dev2**2) / (n1 + n2 - 2))

# Calculate the standard error of the difference

se = pooled_std_dev * np.sqrt((1/n1) + (1/n2))

# Calculate the t-statistic

t_statistic = (mean1 - mean2 - null_mean) / se

# Degrees of freedom

df = n1 + n2 - 2

# Calculate the critical value at a given alpha level (left-tailed test)

alpha = {{ calculationData.significanceLevel }}

critical_value = t.ppf(alpha, df=df)

# Calculate the p-value

p_value = t.cdf(t_statistic, df=df)

# Print the results

print("Mean Difference:", mean1 - mean2)

print("Standard Error:", se)

print("T-Statistic:", t_statistic)

print("P-Value:", p_value)

print("Critical Value:", critical_value)

# Make a decision based on the p-value and critical value

if p_value &lt; alpha and t_statistic &lt; 0:

    print("Reject the null hypothesis. There is evidence of a significant difference between the two samples, with Sample 1 mean being less than Sample 2 mean.")

else:

    print("Fail to reject the null hypothesis. There is no evidence of a significant difference between the two samples or the Sample 1 mean is not significantly less than the Sample 2 mean.")

  </code></pre>

              <!-- right tail -->
              <pre
                v-if="calculationData.method === 'readyData' && currentTail === 'right-tailed'"
                class="language-python"
              ><code id="python">
import numpy as np

from scipy.stats import t

# Calculate the sample means

mean1 = {{ calculationData.meanBefore }}

mean2 = {{ calculationData.meanAfter }}

# Calculate the sample standard deviations

std_dev1 = {{ calculationData.sdOne }}

std_dev2 = {{ calculationData.sdTwo }}

# Sample sizes

n1 = {{ calculationData.sizeOne }}

n2 = {{ calculationData.sizeTwo }}

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is greater than zero

null_mean = {{ calculationData.expectedDifference }}

# Calculate the pooled standard deviation

pooled_std_dev = np.sqrt(((n1-1) * std_dev1**2 + (n2-1) * std_dev2**2) / (n1 + n2 - 2))

# Calculate the standard error of the difference

se = pooled_std_dev * np.sqrt((1/n1) + (1/n2))

# Calculate the t-statistic

t_statistic = (mean1 - mean2 - null_mean) / se

# Degrees of freedom

df = n1 + n2 - 2

# Calculate the critical value at a given alpha level (right-tailed test)

alpha = {{ calculationData.significanceLevel }}

critical_value = t.ppf(1 - alpha, df=df)

# Calculate the p-value

p_value = 1 - t.cdf(t_statistic, df=df)

# Print the results

print("Mean Difference:", mean1 - mean2)

print("Standard Error:", se)

print("T-Statistic:", t_statistic)

print("P-Value:", p_value)

print("Critical Value:", critical_value)

# Make a decision based on the p-value and critical value

if p_value &lt; alpha and t_statistic > 0:

    print("Reject the null hypothesis. There is evidence of a significant difference between the two samples, with Sample 1 mean being greater than Sample 2 mean.")

else:

    print("Fail to reject the null hypothesis. There is no evidence of a significant difference between the two samples or the Sample 1 mean is not significantly greater than the Sample 2 mean.")</code></pre>
              <pre
                v-if="calculationData.method === 'rawData' && currentTail === 'right-tailed'"
                class="language-python"
              ><code id="python">
import numpy as np

from scipy.stats import t

# Sample 1

sample1 = <span class="no-wrap">{{ inputToNumbers[0][0] }}</span>

# Sample 2

sample2 = <span class="no-wrap">{{ inputToNumbers[0][1] }}</span>

# Calculate the sample means

mean1 = np.mean(sample1)

mean2 = np.mean(sample2)

# Calculate the sample standard deviations

std_dev1 = np.std(sample1, ddof=1)

std_dev2 = np.std(sample2, ddof=1)

# Sample sizes

n1 = len(sample1)

n2 = len(sample2)

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is greater than zero

null_mean = {{ calculationData.expectedDifference }}

# Calculate the pooled standard deviation

pooled_std_dev = np.sqrt(((n1-1) * std_dev1**2 + (n2-1) * std_dev2**2) / (n1 + n2 - 2))

# Calculate the standard error of the difference

se = pooled_std_dev * np.sqrt((1/n1) + (1/n2))

# Calculate the t-statistic

t_statistic = (mean1 - mean2 - null_mean) / se

# Degrees of freedom

df = n1 + n2 - 2

# Calculate the critical value at a given alpha level (right-tailed test)

alpha = {{ calculationData.significanceLevel }}

critical_value = t.ppf(1 - alpha, df=df)

# Calculate the p-value

p_value = 1 - t.cdf(t_statistic, df=df)

# Print the results

print("Mean Difference:", mean1 - mean2)

print("Standard Error:", se)

print("T-Statistic:", t_statistic)

print("P-Value:", p_value)

print("Critical Value:", critical_value)

# Make a decision based on the p-value and critical value

if p_value &lt; alpha and t_statistic > 0:

    print("Reject the null hypothesis. There is evidence of a significant difference between the two samples, with Sample 1 mean being greater than Sample 2 mean.")

else:

    print("Fail to reject the null hypothesis. There is no evidence of a significant difference between the two samples or the Sample 1 mean is not significantly greater than the Sample 2 mean.")</code></pre>
            </div>

            <!-- Unequal -->
            <div v-if="calculationData.answer === 'No' && calculationData.sampleAssumption === 'Unequal'">
              <!-- two tails -->
              <pre
                v-if="calculationData.method === 'readyData' && currentTail === 'two-tailed'"
                class="language-python"
              ><code id="python">
import numpy as np

from scipy.stats import t

# Calculate the sample means

mean1 = {{ calculationData.meanBefore }}

mean2 = {{ calculationData.meanAfter }}

# Calculate the sample standard deviations

std_dev1 = {{ calculationData.sdOne }}

std_dev2 = {{ calculationData.sdTwo }}

# Sample sizes

n1 = {{ calculationData.sizeOne }}

n2 = {{ calculationData.sizeTwo }}

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is not equal to zero

null_mean = {{ calculationData.expectedDifference }}

# Calculate the standard error of the difference assuming unequal variances

se = np.sqrt((std_dev1**2 / n1) + (std_dev2**2 / n2))

# Calculate the degrees of freedom assuming unequal variances

df = ((std_dev1**2 / n1 + std_dev2**2 / n2)**2) / ((std_dev1**2 / n1)**2 / (n1 - 1) + (std_dev2**2 / n2)**2 / (n2 - 1))

# Calculate the t-statistic assuming unequal variances

t_statistic = (mean1 - mean2 - null_mean) / se

# Calculate the critical values at a given alpha level (two-tailed test)

alpha = {{ calculationData.significanceLevel }}

critical_value_left = t.ppf(alpha / 2, df=df)

critical_value_right = t.ppf(1 - alpha / 2, df=df)

# Calculate the p-value assuming unequal variances

p_value = 2 * (1 - t.cdf(abs(t_statistic), df=df))

# Print the results

print("Mean Difference:", mean1 - mean2)

print("Standard Error:", se)

print("T-Statistic:", t_statistic)

print("P-Value:", p_value)

print("Critical Values:", critical_value_left, critical_value_right)

# Make a decision based on the p-value and critical values

if p_value &lt; alpha:

if t_statistic &lt; 0:

      print("Reject the null hypothesis. There is evidence of a significant difference between the two samples, with Sample 1 mean being less than Sample 2 mean.")

else:

      print("Reject the null hypothesis. There is evidence of a significant difference between the two samples, with Sample 1 mean being greater than Sample 2 mean.")

else:

      print("Fail to reject the null hypothesis. There is no evidence of a significant difference between the two samples.")</code></pre>
              <pre
                v-if="calculationData.method === 'rawData' && currentTail === 'two-tailed'"
                class="language-python"
              ><code id="python">
import numpy as np

from scipy.stats import t

# Sample 1

sample1 = <span class="no-wrap">{{ inputToNumbers[0][0] }}</span>

# Sample 2

sample2 = <span class="no-wrap">{{ inputToNumbers[0][1] }}</span>

# Calculate the sample means

mean1 = np.mean(sample1)

mean2 = np.mean(sample2)

# Calculate the sample standard deviations

std_dev1 = np.std(sample1, ddof=1)

std_dev2 = np.std(sample2, ddof=1)

# Sample sizes

n1 = len(sample1)

n2 = len(sample2)

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is not equal to zero

null_mean = {{ calculationData.expectedDifference }}

# Calculate the standard error of the difference assuming unequal variances

se = np.sqrt((std_dev1**2 / n1) + (std_dev2**2 / n2))

# Calculate the degrees of freedom assuming unequal variances

df = ((std_dev1**2 / n1 + std_dev2**2 / n2)**2) / ((std_dev1**2 / n1)**2 / (n1 - 1) + (std_dev2**2 / n2)**2 / (n2 - 1))

# Calculate the t-statistic assuming unequal variances

t_statistic = (mean1 - mean2 - null_mean) / se

# Calculate the critical values at a given alpha level (two-tailed test)

alpha = {{ calculationData.significanceLevel }}

critical_value_left = t.ppf(alpha / 2, df=df)

critical_value_right = t.ppf(1 - alpha / 2, df=df)

# Calculate the p-value assuming unequal variances

p_value = 2 * (1 - t.cdf(abs(t_statistic), df=df))

# Print the results

print("Mean Difference:", mean1 - mean2)

print("Standard Error:", se)

print("T-Statistic:", t_statistic)

print("P-Value:", p_value)

print("Critical Values:", critical_value_left, critical_value_right)

# Make a decision based on the p-value and critical values

if p_value &lt; alpha:

if t_statistic &lt; 0:

    print("Reject the null hypothesis. There is evidence of a significant difference between the two samples, with Sample 1 mean being less than Sample 2 mean.")

else:

    print("Reject the null hypothesis. There is evidence of a significant difference between the two samples, with Sample 1 mean being greater than Sample 2 mean.")

else:

    print("Fail to reject the null hypothesis. There is no evidence of a significant difference between the two samples.")</code>
              </pre>

              <!-- left tail -->
              <pre
                v-if="calculationData.method === 'readyData' && currentTail === 'left-tailed'"
                class="language-python"
              ><code id="python">
import numpy as np

from scipy.stats import t

# Calculate the sample means

mean1 = {{ calculationData.meanBefore }}

mean2 = {{ calculationData.meanAfter }}

# Calculate the sample standard deviations

std_dev1 = {{ calculationData.sdOne }}

std_dev2 = {{ calculationData.sdTwo }}

# Sample sizes

n1 = {{ calculationData.sizeOne }}

n2 = {{ calculationData.sizeTwo }}

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is less than zero

null_mean = {{ calculationData.expectedDifference }}

# Calculate the standard error of the difference assuming unequal variances

se = np.sqrt((std_dev1**2 / n1) + (std_dev2**2 / n2))

# Calculate the degrees of freedom assuming unequal variances

df = ((std_dev1**2 / n1 + std_dev2**2 / n2)**2) / ((std_dev1**2 / n1)**2 / (n1 - 1) + (std_dev2**2 / n2)**2 / (n2 - 1))

# Calculate the t-statistic assuming unequal variances

t_statistic = (mean1 - mean2 - null_mean) / se

# Calculate the critical value at a given alpha level (left-tailed test)

alpha = {{ calculationData.significanceLevel }}

critical_value = t.ppf(alpha, df=df)

# Calculate the p-value assuming unequal variances

p_value = t.cdf(t_statistic, df=df)

# Print the results

print("Mean Difference:", mean1 - mean2)

print("Standard Error:", se)

print("T-Statistic:", t_statistic)

print("P-Value:", p_value)

print("Critical Value:", critical_value)

# Make a decision based on the p-value and critical value

if p_value &lt; alpha:

    print("Reject the null hypothesis. There is evidence of a significant difference between the two samples, with Sample 1 mean being less than Sample 2 mean.")

else:

    print("Fail to reject the null hypothesis. There is no evidence of a significant difference between the two samples.")</code></pre>
              <pre
                v-if="calculationData.method === 'rawData' && currentTail === 'left-tailed'"
                class="language-python"
              ><code id="python">
import numpy as np

from scipy.stats import t

# Sample 1

sample1 = <span class="no-wrap">{{ inputToNumbers[0][0] }}</span>

# Sample 2

sample2 = <span class="no-wrap">{{ inputToNumbers[0][1] }}</span>

# Calculate the sample means

mean1 = np.mean(sample1)

mean2 = np.mean(sample2)

# Calculate the sample standard deviations

std_dev1 = np.std(sample1, ddof=1)

std_dev2 = np.std(sample2, ddof=1)

# Sample sizes

n1 = len(sample1)

n2 = len(sample2)

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is less than zero

null_mean = {{ calculationData.expectedDifference }}

# Calculate the standard error of the difference assuming unequal variances

se = np.sqrt((std_dev1**2 / n1) + (std_dev2**2 / n2))

# Calculate the degrees of freedom assuming unequal variances

df = ((std_dev1**2 / n1 + std_dev2**2 / n2)**2) / ((std_dev1**2 / n1)**2 / (n1 - 1) + (std_dev2**2 / n2)**2 / (n2 - 1))

# Calculate the t-statistic assuming unequal variances

t_statistic = (mean1 - mean2 - null_mean) / se

# Calculate the critical value at a given alpha level (left-tailed test)

alpha = {{ calculationData.significanceLevel }}

critical_value = t.ppf(alpha, df=df)

# Calculate the p-value assuming unequal variances

p_value = t.cdf(t_statistic, df=df)

# Print the results

print("Mean Difference:", mean1 - mean2)

print("Standard Error:", se)

print("T-Statistic:", t_statistic)

print("P-Value:", p_value)

print("Critical Value:", critical_value)

# Make a decision based on the p-value and critical value

if p_value &lt; alpha:

    print("Reject the null hypothesis. There is evidence of a significant difference between the two samples, with Sample 1 mean being less than Sample 2 mean.")

else:

    print("Fail to reject the null hypothesis. There is no evidence of a significant difference between the two samples.")</code></pre>

              <!-- right tail -->
              <pre
                v-if="calculationData.method === 'readyData' && currentTail === 'right-tailed'"
                class="language-python"
              ><code id="python">
import numpy as np

from scipy.stats import t

# Calculate the sample means

mean1 = {{ calculationData.meanBefore }}

mean2 = {{ calculationData.meanAfter }}

# Calculate the sample standard deviations

std_dev1 = {{ calculationData.sdOne }}

std_dev2 = {{ calculationData.sdTwo }}

# Sample sizes

n1 = {{ calculationData.sizeOne }}

n2 = {{ calculationData.sizeTwo }}

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is greater than zero

null_mean = {{ calculationData.expectedDifference }}

# Calculate the standard error of the difference assuming unequal variances

se = np.sqrt((std_dev1**2 / n1) + (std_dev2**2 / n2))

# Calculate the degrees of freedom assuming unequal variances

df = ((std_dev1**2 / n1 + std_dev2**2 / n2)**2) / ((std_dev1**2 / n1)**2 / (n1 - 1) + (std_dev2**2 / n2)**2 / (n2 - 1))

# Calculate the t-statistic assuming unequal variances

t_statistic = (mean1 - mean2 - null_mean) / se

# Calculate the critical value at a given alpha level (right-tailed test)

alpha = {{ calculationData.significanceLevel }}

critical_value = t.ppf(1 - alpha, df=df)

# Calculate the p-value assuming unequal variances

p_value = 1 - t.cdf(t_statistic, df=df)

# Print the results

print("Mean Difference:", mean1 - mean2)

print("Standard Error:", se)

print("T-Statistic:", t_statistic)

print("P-Value:", p_value)

print("Critical Value:", critical_value)

# Make a decision based on the p-value and critical value

if p_value &lt; alpha:

    print("Reject the null hypothesis. There is evidence of a significant difference between the two samples, with Sample 1 mean being greater than Sample 2 mean.")

else:

    print("Fail to reject the null hypothesis. There is no evidence of a significant difference between the two samples.")</code></pre>
              <pre
                v-if="calculationData.method === 'rawData' && currentTail === 'right-tailed'"
                class="language-python"
              ><code id="python">
import numpy as np

from scipy.stats import t

# Sample 1

sample1 = <span class="no-wrap">{{ inputToNumbers[0][0] }}</span>

# Sample 2

sample2 = <span class="no-wrap">{{ inputToNumbers[0][1] }}</span>

# Calculate the sample means

mean1 = np.mean(sample1)

mean2 = np.mean(sample2)

# Calculate the sample standard deviations

std_dev1 = np.std(sample1, ddof=1)

std_dev2 = np.std(sample2, ddof=1)

# Sample sizes

n1 = len(sample1)

n2 = len(sample2)

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is greater than zero

null_mean = {{ calculationData.expectedDifference }}

# Calculate the standard error of the difference assuming unequal variances

se = np.sqrt((std_dev1**2 / n1) + (std_dev2**2 / n2))

# Calculate the degrees of freedom assuming unequal variances

df = ((std_dev1**2 / n1 + std_dev2**2 / n2)**2) / ((std_dev1**2 / n1)**2 / (n1 - 1) + (std_dev2**2 / n2)**2 / (n2 - 1))

# Calculate the t-statistic assuming unequal variances

t_statistic = (mean1 - mean2 - null_mean) / se

# Calculate the critical value at a given alpha level (right-tailed test)

alpha = {{ calculationData.significanceLevel }}

critical_value = t.ppf(1 - alpha, df=df)

# Calculate the p-value assuming unequal variances

p_value = 1 - t.cdf(t_statistic, df=df)

# Print the results

print("Mean Difference:", mean1 - mean2)

print("Standard Error:", se)

print("T-Statistic:", t_statistic)

print("P-Value:", p_value)

print("Critical Value:", critical_value)

# Make a decision based on the p-value and critical value

if p_value &lt; alpha:

    print("Reject the null hypothesis. There is evidence of a significant difference between the two samples, with Sample 1 mean being greater than Sample 2 mean.")

else:

    print("Fail to reject the null hypothesis. There is no evidence of a significant difference between the two samples.")</code></pre>
            </div>
          </div>
        </div>
      </div>
      <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedPython === 'Copied' }" @click="copyCode('python')">
        {{ copyCodeOrCopiedPython }}
      </button>
    </div>

    <!-- ----------------------------------------------------------------------------------------------------------------------------------------------- -->

    <!-- R -->
    <div v-if="result" class="python-r-codes">
      <span class="img-text"><img alt="Icon for R language" src="../../../../assets/images/icon-r-calc.png"> <h4 class="h6">Your Result in R:</h4></span>
      <p class="codes-info">
        The following R code should provide the  hypothesis testing using the input data you entered.
      </p>
      <div class="codes-container">
        <div class="codes">
          <div v-if="calculationData.sampleType === 'Dependent'">
            <!-- Yes --------------------------------------------------------------------------------------------------- -->

            <div v-if="calculationData.answer === 'Yes'">
              <!-- two tails -->
              <pre
                v-if="calculationData.method === 'readyData' && currentTail === 'two-tailed'"
                class="language-r"
              ><code id="r">
# Given data

sample_mean_diff &lt;- {{ calculationData.mean }}

sample_std_dev_diff &lt;- {{ calculationData.standardDeviation }}

n &lt;- {{ calculationData.size }}

# Define the null mean (typically 0 for paired t-tests)

null_mean &lt;- {{ calculationData.expectedDifference }}

# Calculate the t-statistic

t_statistic &lt;- (sample_mean_diff - null_mean) / (sample_std_dev_diff / sqrt(n))

# Calculate the degrees of freedom

df &lt;- n - 1

# Calculate the p-value for two-sided test

p_value &lt;- 2 * (1 - pt(abs(t_statistic), df))

# Print the results

cat("Sample Mean Difference:", sample_mean_diff, "\n")

cat("T-Statistic:", t_statistic, "\n")

cat("P-Value:", p_value, "\n")

# Make a decision based on the p-value

alpha &lt;- {{ calculationData.significanceLevel }}

if (p_value &lt; alpha) {

  cat("Reject the null hypothesis. There is evidence of a significant difference from", null_mean, ".\n")

} else {

  cat("Fail to reject the null hypothesis. There is no evidence of a significant difference from", null_mean, ".\n")

}
</code></pre>
              <pre
                v-if="calculationData.method === 'rawData' && currentTail === 'two-tailed'"
                class="language-r"
              ><code id="r">
# Paired data

before &lt;- c(<span class="no-wrap">{{ inputToNumbers[1][0] }}</span>)

after &lt;- c(<span class="no-wrap">{{ inputToNumbers[1][1] }}</span>)

# Calculate the differences between paired observations

differences &lt;- after - before

# Sample size

n &lt;- length(differences)

# Known standard deviation

std_dev &lt;- {{ calculationData.standardDeviation }}

# Calculate the sample mean of the differences

mean_diff &lt;- mean(differences)

# Calculate the standard error of the mean difference

se &lt;- std_dev / sqrt(n)

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is not zero (two-tailed test)

null_mean &lt;- {{ calculationData.expectedDifference }}

# Calculate the z-statistic

z_statistic &lt;- (mean_diff - null_mean) / se

# Calculate the critical values at a given alpha level (two-tailed test)

alpha &lt;- {{ calculationData.significanceLevel }}
critical_value &lt;- qnorm(1 - alpha / 2)

# Calculate the p-value

p_value &lt;- 2 * (1 - pnorm(abs(z_statistic)))

# Print the results

cat("Sample Mean Difference:", mean_diff, "\n")

cat("Standard Error:", se, "\n")

cat("Z-Statistic:", z_statistic, "\n")

cat("P-Value:", p_value, "\n")

cat("Critical Values:", -critical_value, critical_value, "\n")

# Make a decision based on the p-value and critical values

if (p_value &lt; alpha) {

  cat("Reject the null hypothesis. There is a significant difference in means.\n")

} else {

  cat("Fail to reject the null hypothesis. There is no significant difference in means.\n")

}
</code></pre>

              <!-- left tail -->
              <pre
                v-if="calculationData.method === 'readyData' && currentTail === 'left-tailed'"
                class="language-r"
              ><code id="r">
# Given data

sample_mean_diff &lt;- {{ calculationData.mean }}

sample_std_dev_diff &lt;- {{ calculationData.standardDeviation }}

n &lt;- {{ calculationData.size }}

# Define the null mean

null_mean &lt;- {{ calculationData.expectedDifference }}

# Calculate the t-statistic

t_statistic &lt;- (sample_mean_diff - null_mean) / (sample_std_dev_diff / sqrt(n))

# Calculate the degrees of freedom

df &lt;- n - 1

# Calculate the p-value for one-sided test

p_value &lt;- pt(t_statistic, df)

# Print the results

cat("Sample Mean Difference:", sample_mean_diff, "\n")

cat("T-Statistic:", t_statistic, "\n")

cat("P-Value:", p_value, "\n")

# Make a decision based on the p-value

alpha &lt;- {{ calculationData.significanceLevel }}

if (p_value &lt; alpha) {

  cat("Reject the null hypothesis. There is evidence of a significant decrease.\n")

} else {

  cat("Fail to reject the null hypothesis. There is no evidence of a significant decrease.\n")

}
</code></pre>
              <pre
                v-if="calculationData.method === 'rawData' && currentTail === 'left-tailed'"
                class="language-r"
              ><code id="r">
# Paired data

before &lt;- c(<span class="no-wrap">{{ inputToNumbers[1][0] }}</span>)

after &lt;- c(<span class="no-wrap">{{ inputToNumbers[1][1] }}</span>)

# Calculate the differences between paired observations

differences &lt;- after - before

# Sample size

n &lt;- length(differences)

# Known standard deviation

std_dev &lt;- {{ calculationData.standardDeviation }}

# Calculate the sample mean of the differences

mean_diff &lt;- mean(differences)

# Calculate the standard error of the mean difference

se &lt;- std_dev / sqrt(n)

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is less than zero (left-tailed test)

null_mean &lt;- {{ calculationData.expectedDifference }}

# Calculate the z-statistic

z_statistic &lt;- (mean_diff - null_mean) / se

# Calculate the critical value at a given alpha level (left-tailed test)

alpha &lt;- {{ calculationData.significanceLevel }}
critical_value &lt;- qnorm(1 - alpha)

# Calculate the p-value

p_value &lt;- pnorm(z_statistic)

# Print the results

cat("Sample Mean Difference:", mean_diff, "\n")

cat("Standard Error:", se, "\n")

cat("Z-Statistic:", z_statistic, "\n")

cat("P-Value:", p_value, "\n")

cat("Critical Value:", critical_value, "\n")

# Make a decision based on the p-value and critical value

if (p_value &lt; alpha) {

  cat("Reject the null hypothesis. The mean difference is less than zero.\n")

} else {

  cat("Fail to reject the null hypothesis. There is no significant evidence that the mean difference is less than zero.\n")

}
</code></pre>

              <!-- right tail -->
              <pre
                v-if="calculationData.method === 'readyData' && currentTail === 'right-tailed'"
                class="language-r"
              ><code id="r">
# Given data

sample_mean_diff &lt;- {{ calculationData.mean }}

sample_std_dev_diff &lt;- {{ calculationData.standardDeviation }}

n &lt;- {{ calculationData.size }}

# Define the null mean

null_mean &lt;- {{ calculationData.expectedDifference }}

# Calculate the t-statistic

t_statistic &lt;- (sample_mean_diff - null_mean) / (sample_std_dev_diff / sqrt(n))

# Calculate the degrees of freedom

df &lt;- n - 1

# Calculate the p-value for right-tailed test

p_value &lt;- pt(-t_statistic, df)  # Note the negative sign before t_statistic for a right-tailed test

# Print the results

cat("Sample Mean Difference:", sample_mean_diff, "\n")

cat("T-Statistic:", t_statistic, "\n")

cat("P-Value:", p_value, "\n")

# Make a decision based on the p-value

alpha &lt;- {{ calculationData.significanceLevel }}

if (p_value &lt; alpha) {

  cat("Reject the null hypothesis. There is evidence of a significant increase.\n")

} else {

  cat("Fail to reject the null hypothesis. There is no evidence of a significant increase.\n")

}
</code>
            </pre>
              <pre
                v-if="calculationData.method === 'rawData' && currentTail === 'right-tailed'"
                class="language-r"
              ><code id="r">
# Paired data

before &lt;- c(<span class="no-wrap">{{ inputToNumbers[1][0] }}</span>)

after &lt;- c(<span class="no-wrap">{{ inputToNumbers[1][1] }}</span>)

# Define the null mean

null_mean &lt;- {{ calculationData.expectedDifference }}

# Perform the paired t-test with mu (expected mean difference under the null hypothesis)

t_test &lt;- t.test(after, before, paired = TRUE, alternative = "greater", mu = null_mean)

# Print the results

cat("Sample Mean Difference:", mean(after - before), "\n")

cat("T-Statistic:", t_test$statistic, "\n")

cat("P-Value:", t_test$p.value, "\n")

cat("Confidence Interval:", t_test$conf.int, "\n")

# Make a decision based on the p-value

alpha &lt;- {{ calculationData.significanceLevel }}

if (t_test$p.value &lt; alpha) {

  cat("Reject the null hypothesis. There is evidence of a significant increase.\n")

} else {

  cat("Fail to reject the null hypothesis. There is no evidence of a significant increase.\n")

}

</code></pre>
            </div>

            <!-- No --------------------------------------------------------------------------------------------------- -->

            <div v-if="calculationData.answer === 'No'">
              <!-- two tails -->
              <pre
                v-if="calculationData.method === 'readyData' && currentTail === 'two-tailed'"
                class="language-r"
              ><code id="r">
# Given values

sample_mean_diff &lt;- {{ calculationData.mean }}

sample_std_dev_diff &lt;- {{ calculationData.standardDeviation }}

n &lt;- {{ calculationData.size }}

# Define the null mean (typically 0 for paired t-tests)

null_mean &lt;- {{ calculationData.expectedDifference }}

# Calculate the t-statistic

t_statistic &lt;- (sample_mean_diff - null_mean) / (sample_std_dev_diff / sqrt(n))

# Calculate the degrees of freedom

df &lt;- n - 1

# Calculate the p-value for two-sided test

p_value &lt;- 2 * (1 - pt(abs(t_statistic), df))

# Print the results

cat("Sample Mean Difference:", sample_mean_diff, "\n")

cat("T-Statistic:", t_statistic, "\n")

cat("P-Value:", p_value, "\n")

# Make a decision based on the p-value

alpha &lt;- {{ calculationData.significanceLevel }}

if (p_value &lt; alpha) {

  cat("Reject the null hypothesis. There is evidence of a significant difference from", null_mean, ".\n")

} else {

  cat("Fail to reject the null hypothesis. There is no evidence of a significant difference from", null_mean, ".\n")

}
</code></pre>
              <pre
                v-if="calculationData.method === 'rawData' && currentTail === 'two-tailed'"
                class="language-r"
              ><code id="r">
# Paired data

before &lt;- c(<span class="no-wrap">{{ inputToNumbers[1][0] }}</span>)

after &lt;- c(<span class="no-wrap">{{ inputToNumbers[1][1] }}</span>)

# Define the null mean

null_mean &lt;- {{ calculationData.expectedDifference }}

# Calculate the observed mean difference

observed_mean_diff &lt;- mean(after - before)

# Perform the paired t-test

t_test &lt;- t.test(after, before, paired = TRUE, alternative = "two.sided", mu = null_mean)

# Print the results

cat("Sample Mean Difference:", observed_mean_diff, "\n")

cat("T-Statistic:", t_test$statistic, "\n")

cat("P-Value:", t_test$p.value, "\n")

cat("Confidence Interval:", t_test$conf.int, "\n")

# Make a decision based on the p-value

alpha &lt;- {{ calculationData.significanceLevel }}

if (t_test$p.value &lt; alpha) {

  cat("Reject the null hypothesis. There is evidence of a significant difference from", null_mean, ".\n")

} else {

  cat("Fail to reject the null hypothesis. There is no evidence of a significant difference from", null_mean, ".\n")

}

</code></pre>

              <!-- left tail -->
              <pre
                v-if="calculationData.method === 'readyData' && currentTail === 'left-tailed'"
                class="language-r"
              ><code id="r">
# Given data

sample_mean_diff &lt;- {{ calculationData.mean }}

sample_std_dev_diff &lt;- {{ calculationData.standardDeviation }}

n &lt;- {{ calculationData.size }}

# Define the null mean

null_mean &lt;- {{ calculationData.expectedDifference }}

# Calculate the t-statistic

t_statistic &lt;- (sample_mean_diff - null_mean) / (sample_std_dev_diff / sqrt(n))

# Calculate the degrees of freedom

df &lt;- n - 1

# Calculate the p-value for one-sided test

p_value &lt;- pt(t_statistic, df)

# Print the results

cat("Sample Mean Difference:", sample_mean_diff, "\n")

cat("T-Statistic:", t_statistic, "\n")

cat("P-Value:", p_value, "\n")

# Make a decision based on the p-value

alpha &lt;- {{ calculationData.significanceLevel }}

if (p_value &lt; alpha) {

  cat("Reject the null hypothesis. There is evidence of a significant decrease.\n")

} else {

  cat("Fail to reject the null hypothesis. There is no evidence of a significant decrease.\n")

}
</code></pre>
              <pre
                v-if="calculationData.method === 'rawData' && currentTail === 'left-tailed'"
                class="language-r"
              ><code id="r">
# Paired data

before &lt;- c(<span class="no-wrap">{{ inputToNumbers[1][0] }}</span>)

after &lt;- c(<span class="no-wrap">{{ inputToNumbers[1][1] }}</span>)

# Define the null mean

null_mean &lt;- {{ calculationData.expectedDifference }}

# Perform the paired t-test with mu (expected mean difference under the null hypothesis)

t_test &lt;- t.test(after, before, paired = TRUE, alternative = "less", mu = null_mean)

# Print the results

cat("Sample Mean Difference:", mean(after - before), "\n")

cat("T-Statistic:", t_test$statistic, "\n")

cat("P-Value:", t_test$p.value, "\n")

cat("Confidence Interval:", t_test$conf.int, "\n")

# Make a decision based on the p-value

alpha &lt;- {{ calculationData.significanceLevel }}

if (t_test$p.value &lt; alpha) {

  cat("Reject the null hypothesis. There is evidence of a significant decrease.\n")

} else {

  cat("Fail to reject the null hypothesis. There is no evidence of a significant decrease.\n")

}
</code></pre>

              <!-- right tail -->
              <pre
                v-if="calculationData.method === 'readyData' && currentTail === 'right-tailed'"
                class="language-r"
              ><code id="r">
# Given data

sample_mean_diff &lt;- {{ calculationData.mean }}

sample_std_dev_diff &lt;- {{ calculationData.standardDeviation }}

n &lt;- {{ calculationData.size }}

# Define the null mean

null_mean &lt;- {{ calculationData.expectedDifference }}

# Calculate the t-statistic

t_statistic &lt;- (sample_mean_diff - null_mean) / (sample_std_dev_diff / sqrt(n))

# Calculate the degrees of freedom

df &lt;- n - 1

# Calculate the p-value for right-tailed test

p_value &lt;- pt(-t_statistic, df)  # Note the negative sign before t_statistic for a right-tailed test

# Print the results

cat("Sample Mean Difference:", sample_mean_diff, "\n")

cat("T-Statistic:", t_statistic, "\n")

cat("P-Value:", p_value, "\n")

# Make a decision based on the p-value

alpha &lt;- {{ calculationData.significanceLevel }}

if (p_value &lt; alpha) {

  cat("Reject the null hypothesis. There is evidence of a significant increase.\n")

} else {

  cat("Fail to reject the null hypothesis. There is no evidence of a significant increase.\n")

}
</code></pre>
              <pre
                v-if="calculationData.method === 'rawData' && currentTail === 'right-tailed'"
                class="language-r"
              ><code id="r">
# Paired data

before &lt;- c(<span class="no-wrap">{{ inputToNumbers[1][0] }}</span>)

after &lt;- c(<span class="no-wrap">{{ inputToNumbers[1][1] }}</span>)

# Define the null mean

null_mean &lt;- {{ calculationData.expectedDifference }}

# Perform the paired t-test with mu (expected mean difference under the null hypothesis)

t_test &lt;- t.test(after, before, paired = TRUE, alternative = "greater", mu = null_mean)

# Print the results

cat("Sample Mean Difference:", mean(after - before), "\n")

cat("T-Statistic:", t_test$statistic, "\n")

cat("P-Value:", t_test$p.value, "\n")

cat("Confidence Interval:", t_test$conf.int, "\n")

# Make a decision based on the p-value

alpha &lt;- {{ calculationData.significanceLevel }}

if (t_test$p.value &lt; alpha) {

  cat("Reject the null hypothesis. There is evidence of a significant increase.\n")

} else {

  cat("Fail to reject the null hypothesis. There is no evidence of a significant increase.\n")

}
</code></pre>
            </div>
          </div>

          <!-- ------------------------------------------------------------------------------------------------------------------------------------------------------------ -->

          <div v-if="calculationData.sampleType === 'Independent'">
            <!-- Yes --------------------------------------------------------------------------------------------------- -->

            <div v-if="calculationData.answer === 'Yes'">
              <!-- two tails -->
              <pre
                v-if="calculationData.method === 'readyData' && currentTail === 'two-tailed'"
                class="language-r"
              ><code id="r">
# Population standard deviations

std_dev1 &lt;- {{ calculationData.sdOne }}

std_dev2 &lt;- {{ calculationData.sdTwo }}

# Sample sizes

n1 &lt;- {{ calculationData.sizeOne }}

n2 &lt;- {{ calculationData.sizeTwo }}

# Provided mean difference

mean_diff &lt;- {{ calculationData.mean }}

# Calculate the standard error of the difference

se &lt;- sqrt((std_dev1^2/n1) + (std_dev2^2/n2))

# Alternative hypothesis: The mean difference is different from zero

null_mean &lt;- {{ calculationData.expectedDifference }}

# Calculate the z-statistic using the formula

z_statistic &lt;- (mean_diff - null_mean) / se

# Calculate the critical value for alpha = {{ calculationData.significanceLevel }} (two-tailed)

critical_value &lt;- qnorm(1 - {{ significanceLevelDividedByTwo }})

# Print the results

cat("Mean Difference:", mean_diff, "\n")

cat("Standard Error:", se, "\n")

cat("Z-Statistic:", z_statistic, "\n")

cat("Critical Value:", critical_value, "\n")

# Make a decision based on the z-statistic

if (abs(z_statistic) > critical_value) {

    cat("Reject the null hypothesis. There is evidence of a significant difference between the two samples.\n")

} else {

    cat("Fail to reject the null hypothesis. There is no evidence of a significant difference between the two samples.\n")

}
</code></pre>
              <pre
                v-if="calculationData.method === 'rawData' && currentTail === 'two-tailed'"
                class="language-r"
              ><code id="r">
# Sample 1

sample1 &lt;- c({{ inputToNumbers[1][0] }})

# Sample 2

sample2 &lt;- c({{ inputToNumbers[1][1] }})

# Population standard deviations

std_dev1 &lt;- {{ calculationData.sdOne }}

std_dev2 &lt;- {{ calculationData.sdTwo }}

# Sample sizes

n1 &lt;- length(sample1)

n2 &lt;- length(sample2)

# Calculate the mean difference

mean_diff &lt;- mean(sample1) - mean(sample2)

# Calculate the standard error of the difference

se &lt;- sqrt((std_dev1^2/n1) + (std_dev2^2/n2))

# Alternative hypothesis: The mean difference is different from zero

null_mean &lt;- {{ calculationData.expectedDifference }}

# Calculate the z-statistic using the formula

z_statistic &lt;- mean_diff / se

# Calculate the critical value for alpha = {{ calculationData.significanceLevel }} (two-tailed)

critical_value &lt;- qnorm(1 - {{ significanceLevelDividedByTwo }})

# Print the results

cat("Mean Difference:", mean_diff, "\n")

cat("Standard Error:", se, "\n")

cat("Z-Statistic:", z_statistic, "\n")

cat("Critical Value:", critical_value, "\n")

# Make a decision based on the z-statistic

if (abs(z_statistic) > critical_value) {

    cat("Reject the null hypothesis. There is evidence of a significant difference between the two samples.\n")

} else {

    cat("Fail to reject the null hypothesis. There is no evidence of a significant difference between the two samples.\n")

}

</code></pre>

              <!-- left tail -->
              <pre
                v-if="calculationData.method === 'readyData' && currentTail === 'left-tailed'"
                class="language-r"
              ><code id="r">
# Population standard deviations

std_dev1 &lt;- {{ calculationData.sdOne }}

std_dev2 &lt;- {{ calculationData.sdTwo }}

# Sample sizes

n1 &lt;- {{ calculationData.sizeOne }}

n2 &lt;- {{ calculationData.sizeTwo }}

# Calculate the mean difference

mean_diff &lt;- {{ calculationData.mean }}

# Calculate the standard error of the difference

se &lt;- sqrt((std_dev1^2/n1) + (std_dev2^2/n2))

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is less than zero

null_mean &lt;- {{ calculationData.expectedDifference }}

# Calculate the z-statistic

z_statistic &lt;- (mean_diff - null_mean) / se

# Print the results

cat("Mean Difference:", mean_diff, "\n")

cat("Standard Error:", se, "\n")

cat("Z-Statistic:", z_statistic, "\n")

# Make a decision based on the z-statistic

alpha &lt;- {{ calculationData.significanceLevel }}

if (z_statistic &lt; -qnorm(1-alpha)) {

    cat("Reject the null hypothesis. There is evidence of a significant decrease.\n")

} else {

    cat("Fail to reject the null hypothesis. There is no evidence of a significant decrease.\n")

}
</code></pre>
              <pre
                v-if="calculationData.method === 'rawData' && currentTail === 'left-tailed'"
                class="language-r"
              ><code id="r">
# Sample 1

sample1 &lt;- c({{ inputToNumbers[1][0] }})

# Sample 2

sample2 &lt;- c({{ inputToNumbers[1][1] }})

# Population standard deviations

std_dev1 &lt;- {{ calculationData.sdOne }}

std_dev2 &lt;- {{ calculationData.sdTwo }}

# Sample sizes

n1 &lt;- length(sample1)

n2 &lt;- length(sample2)

# Calculate the mean difference

mean_diff &lt;- mean(sample1) - mean(sample2)

# Calculate the standard error of the difference

se &lt;- sqrt((std_dev1^2/n1) + (std_dev2^2/n2))

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is less than zero

null_mean &lt;- {{ calculationData.expectedDifference }}

# Calculate the z-statistic

z_statistic &lt;- (mean_diff - null_mean) / se

# Print the results

cat("Mean Difference:", mean_diff, "\n")

cat("Standard Error:", se, "\n")

cat("Z-Statistic:", z_statistic, "\n")

# Make a decision based on the z-statistic

alpha &lt;- {{ calculationData.significanceLevel }}

if (z_statistic &lt; -qnorm(1-alpha)) {

    cat("Reject the null hypothesis. There is evidence of a significant decrease.\n")

} else {

    cat("Fail to reject the null hypothesis. There is no evidence of a significant decrease.\n")

}
</code></pre>

              <!-- right tail -->
              <pre
                v-if="calculationData.method === 'readyData' && currentTail === 'right-tailed'"
                class="language-r"
              ><code id="r">
# Population standard deviations

std_dev1 &lt;- {{ calculationData.sdOne }}

std_dev2 &lt;- {{ calculationData.sdTwo }}

# Sample sizes

n1 &lt;- {{ calculationData.sizeOne }}

n2 &lt;- {{ calculationData.sizeTwo }}

# Calculate the mean difference

mean_diff &lt;- {{ calculationData.mean }}

# Calculate the standard error of the difference

se &lt;- sqrt((std_dev1^2/n1) + (std_dev2^2/n2))

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is greater than zero

null_mean &lt;- {{ calculationData.expectedDifference }}

# Calculate the z-statistic

z_statistic &lt;- (mean_diff - null_mean) / se

# Print the results

cat("Mean Difference:", mean_diff, "\n")

cat("Standard Error:", se, "\n")

cat("Z-Statistic:", z_statistic, "\n")

# Make a decision based on the z-statistic

alpha &lt;- {{ calculationData.significanceLevel }}

if (z_statistic > qnorm(1-alpha)) {

    cat("Reject the null hypothesis. There is evidence of a significant increase.\n")

} else {

    cat("Fail to reject the null hypothesis. There is no evidence of a significant increase.\n")

}
</code>
            </pre>
              <pre
                v-if="calculationData.method === 'rawData' && currentTail === 'right-tailed'"
                class="language-r"
              ><code id="r">
# Sample 1

sample1 &lt;- c({{ inputToNumbers[1][0] }})

# Sample 2

sample2 &lt;- c({{ inputToNumbers[1][1] }})

# Population standard deviations

std_dev1 &lt;- {{ calculationData.sdOne }}

std_dev2 &lt;- {{ calculationData.sdTwo }}

# Sample sizes

n1 &lt;- length(sample1)

n2 &lt;- length(sample2)

# Calculate the mean difference

mean_diff &lt;- mean(sample1) - mean(sample2)

# Calculate the standard error of the difference

se &lt;- sqrt((std_dev1^2/n1) + (std_dev2^2/n2))

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is greater than zero

null_mean &lt;- {{ calculationData.expectedDifference }}

# Calculate the z-statistic

z_statistic &lt;- (mean_diff - null_mean) / se

# Print the results

cat("Mean Difference:", mean_diff, "\n")

cat("Standard Error:", se, "\n")

cat("Z-Statistic:", z_statistic, "\n")

# Make a decision based on the z-statistic

alpha &lt;- {{ calculationData.significanceLevel }}

if (z_statistic > qnorm(1-alpha)) {

    cat("Reject the null hypothesis. There is evidence of a significant increase.\n")

} else {

    cat("Fail to reject the null hypothesis. There is no evidence of a significant increase.\n")

}
</code></pre>
            </div>

            <!-- No --------------------------------------------------------------------------------------------------- -->

            <!-- Equal -->
            <div v-if="calculationData.answer === 'No' && calculationData.sampleAssumption === 'Equal'">
              <!-- two tails -->
              <pre
                v-if="calculationData.method === 'readyData' && calculationData.answer === 'No' && currentTail === 'two-tailed'"
                class="language-r"
              ><code id="r">
# Calculate the sample means

mean1 &lt;- {{ calculationData.meanBefore }}

mean2 &lt;- {{ calculationData.meanAfter }}

# Calculate the sample standard deviations

std_dev1 &lt;- {{ calculationData.sdOne }}

std_dev2 &lt;- {{ calculationData.sdTwo }}

# Sample sizes

n1 &lt;- {{ calculationData.sizeOne }}

n2 &lt;- {{ calculationData.sizeTwo }}

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is not zero

null_mean &lt;- {{ calculationData.expectedDifference }}

# Calculate the pooled standard deviation

pooled_std_dev &lt;- sqrt(((n1-1) * std_dev1^2 + (n2-1) * std_dev2^2) / (n1 + n2 - 2))

# Calculate the standard error of the difference

se &lt;- pooled_std_dev * sqrt((1/n1) + (1/n2))

# Calculate the t-statistic

t_statistic &lt;- (mean1 - mean2 - null_mean) / se

# Degrees of freedom

df &lt;- n1 + n2 - 2

# Calculate the critical values at a given alpha level (two-tailed test)

alpha &lt;- {{ calculationData.significanceLevel }}

critical_value_left &lt;- qt(alpha/2, df=df)

critical_value_right &lt;- qt(1-alpha/2, df=df)

# Calculate the p-value

p_value &lt;- 2 * (1 - pt(abs(t_statistic), df=df))

# Print the results

cat("Mean Difference:", mean1 - mean2, "\n")

cat("Standard Error:", se, "\n")

cat("T-Statistic:", t_statistic, "\n")

cat("P-Value:", p_value, "\n")

cat("Critical Values:", critical_value_left, critical_value_right, "\n")

# Make a decision based on the p-value and critical values

if (p_value &lt; alpha) {

  cat("Reject the null hypothesis. There is evidence of a significant difference between the two samples.\n")

} else {

  cat("Fail to reject the null hypothesis. There is no evidence of a significant difference between the two samples.\n")

}
</code></pre>
              <pre
                v-if="calculationData.method === 'rawData' && calculationData.answer === 'No' && currentTail === 'two-tailed'"
                class="language-r"
              ><code id="r">
# Sample 1

sample1 &lt;- c({{ inputToNumbers[1][0] }})

# Sample 2

sample2 &lt;- c({{ inputToNumbers[1][1] }})

# Calculate the sample means

mean1 &lt;- mean(sample1)

mean2 &lt;- mean(sample2)

# Calculate the sample standard deviations

std_dev1 &lt;- sd(sample1)

std_dev2 &lt;- sd(sample2)

# Sample sizes

n1 &lt;- length(sample1)

n2 &lt;- length(sample2)

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is not zero

null_mean &lt;- {{ calculationData.expectedDifference }}

# Calculate the pooled standard deviation

pooled_std_dev &lt;- sqrt(((n1-1) * std_dev1^2 + (n2-1) * std_dev2^2) / (n1 + n2 - 2))

# Calculate the standard error of the difference

se &lt;- pooled_std_dev * sqrt((1/n1) + (1/n2))

# Calculate the t-statistic

t_statistic &lt;- (mean1 - mean2 - null_mean) / se

# Degrees of freedom

df &lt;- n1 + n2 - 2

# Calculate the critical values at a given alpha level (two-tailed test)

alpha &lt;- {{ calculationData.significanceLevel }}

critical_value_left &lt;- qt(alpha/2, df=df)

critical_value_right &lt;- qt(1-alpha/2, df=df)

# Calculate the p-value

p_value &lt;- 2 * (1 - pt(abs(t_statistic), df=df))

# Print the results

cat("Mean Difference:", mean1 - mean2, "\n")

cat("Standard Error:", se, "\n")

cat("T-Statistic:", t_statistic, "\n")

cat("P-Value:", p_value, "\n")

cat("Critical Values:", critical_value_left, critical_value_right, "\n")

# Make a decision based on the p-value and critical values

if (p_value &lt; alpha) {

  cat("Reject the null hypothesis. There is evidence of a significant difference between the two samples.\n")

} else {

  cat("Fail to reject the null hypothesis. There is no evidence of a significant difference between the two samples.\n")

}
</code></pre>

              <!-- left tail -->
              <pre
                v-if="calculationData.method === 'readyData' && calculationData.answer === 'No' && currentTail === 'left-tailed'"
                class="language-r"
              ><code id="r">
# Calculate the sample means

mean1 &lt;- {{ calculationData.meanBefore }}

mean2 &lt;- {{ calculationData.meanAfter }}

# Calculate the sample standard deviations

std_dev1 &lt;- {{ calculationData.sdOne }}

std_dev2 &lt;- {{ calculationData.sdTwo }}

# Sample sizes

n1 &lt;- {{ calculationData.sizeOne }}

n2 &lt;- {{ calculationData.sizeTwo }}

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is less than zero (left-tailed test)

null_mean &lt;- {{ calculationData.expectedDifference }}

# Calculate the pooled standard deviation

pooled_std_dev &lt;- sqrt(((n1-1) * std_dev1^2 + (n2-1) * std_dev2^2) / (n1 + n2 - 2))

# Calculate the standard error of the difference

se &lt;- pooled_std_dev * sqrt((1/n1) + (1/n2))

# Calculate the t-statistic

t_statistic &lt;- (mean1 - mean2 - null_mean) / se

# Degrees of freedom

df &lt;- n1 + n2 - 2

# Calculate the critical value at a given alpha level (left-tailed test)

alpha &lt;- {{ calculationData.significanceLevel }}

critical_value &lt;- qt(alpha, df=df)

# Calculate the p-value

p_value &lt;- pt(t_statistic, df=df)

# Print the results

cat("Mean Difference:", mean1 - mean2, "\n")

cat("Standard Error:", se, "\n")

cat("T-Statistic:", t_statistic, "\n")

cat("P-Value:", p_value, "\n")

cat("Critical Value:", critical_value, "\n")

# Make a decision based on the p-value and critical value

if (p_value &lt; alpha && t_statistic &lt; 0) {

  cat("Reject the null hypothesis. There is evidence of a significant decrease in the mean difference.\n")

} else {

  cat("Fail to reject the null hypothesis. There is no evidence of a significant decrease in the mean difference.\n")

}
</code></pre>
              <pre
                v-if="calculationData.method === 'rawData' && calculationData.answer === 'No' && currentTail === 'left-tailed'"
                class="language-r"
              ><code id="r">
# Sample 1

sample1 &lt;- c({{ inputToNumbers[1][0] }})

# Sample 2

sample2 &lt;- c({{ inputToNumbers[1][1] }})

# Calculate the sample means

mean1 &lt;- mean(sample1)

mean2 &lt;- mean(sample2)

# Calculate the sample standard deviations

std_dev1 &lt;- sd(sample1)

std_dev2 &lt;- sd(sample2)

# Sample sizes

n1 &lt;- length(sample1)

n2 &lt;- length(sample2)

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is less than zero (left-tailed test)

null_mean &lt;- {{ calculationData.expectedDifference }}

# Calculate the pooled standard deviation

pooled_std_dev &lt;- sqrt(((n1-1) * std_dev1^2 + (n2-1) * std_dev2^2) / (n1 + n2 - 2))

# Calculate the standard error of the difference

se &lt;- pooled_std_dev * sqrt((1/n1) + (1/n2))

# Calculate the t-statistic

t_statistic &lt;- (mean1 - mean2 - null_mean) / se

# Degrees of freedom

df &lt;- n1 + n2 - 2

# Calculate the critical value at a given alpha level (left-tailed test)

alpha &lt;- {{ calculationData.significanceLevel }}

critical_value &lt;- qt(alpha, df=df)

# Calculate the p-value

p_value &lt;- pt(t_statistic, df=df)

# Print the results

cat("Mean Difference:", mean1 - mean2, "\n")

cat("Standard Error:", se, "\n")

cat("T-Statistic:", t_statistic, "\n")

cat("P-Value:", p_value, "\n")

cat("Critical Value:", critical_value, "\n")

# Make a decision based on the p-value and critical value

if (p_value &lt; alpha && t_statistic &lt; 0) {

  cat("Reject the null hypothesis. There is evidence of a significant decrease in the mean difference.\n")

} else {

  cat("Fail to reject the null hypothesis. There is no evidence of a significant decrease in the mean difference.\n")

}
</code></pre>

              <!-- right tail -->
              <pre
                v-if="calculationData.method === 'readyData' && calculationData.answer === 'No' && currentTail === 'right-tailed'"
                class="language-r"
              ><code id="r">
# Calculate the sample means

mean1 &lt;- {{ calculationData.meanBefore }}

mean2 &lt;- {{ calculationData.meanAfter }}

# Calculate the sample standard deviations

std_dev1 &lt;- {{ calculationData.sdOne }}

std_dev2 &lt;- {{ calculationData.sdTwo }}

# Sample sizes

n1 &lt;- {{ calculationData.sizeOne }}

n2 &lt;- {{ calculationData.sizeTwo }}

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is greater than zero (right-tailed test)

null_mean &lt;- {{ calculationData.expectedDifference }}

# Calculate the pooled standard deviation

pooled_std_dev &lt;- sqrt(((n1-1) * std_dev1^2 + (n2-1) * std_dev2^2) / (n1 + n2 - 2))

# Calculate the standard error of the difference

se &lt;- pooled_std_dev * sqrt((1/n1) + (1/n2))

# Calculate the t-statistic

t_statistic &lt;- (mean1 - mean2 - null_mean) / se

# Degrees of freedom

df &lt;- n1 + n2 - 2

# Calculate the critical value at a given alpha level (right-tailed test)

alpha &lt;- {{ calculationData.significanceLevel }}

critical_value &lt;- qt(1 - alpha, df=df)

# Calculate the p-value

p_value &lt;- pt(t_statistic, df=df, lower.tail=FALSE)

# Print the results

cat("Mean Difference:", mean1 - mean2, "\n")

cat("Standard Error:", se, "\n")

cat("T-Statistic:", t_statistic, "\n")

cat("P-Value:", p_value, "\n")

cat("Critical Value:", critical_value, "\n")

# Make a decision based on the p-value and critical value

if (p_value &lt; alpha && t_statistic > 0) {

  cat("Reject the null hypothesis. There is evidence of a significant increase in the mean difference.\n")

} else {

  cat("Fail to reject the null hypothesis. There is no evidence of a significant increase in the mean difference.\n")

}
</code></pre>
              <pre
                v-if="calculationData.method === 'rawData' && calculationData.answer === 'No' && currentTail === 'right-tailed'"
                class="language-r"
              ><code id="r">
# Sample 1

sample1 &lt;- c({{ inputToNumbers[1][0] }})

# Sample 2

sample2 &lt;- c({{ inputToNumbers[1][1] }})

# Calculate the sample means

mean1 &lt;- mean(sample1)

mean2 &lt;- mean(sample2)

# Calculate the sample standard deviations

std_dev1 &lt;- sd(sample1)

std_dev2 &lt;- sd(sample2)

# Sample sizes

n1 &lt;- length(sample1)

n2 &lt;- length(sample2)

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is greater than zero (right-tailed test)

null_mean &lt;- {{ calculationData.expectedDifference }}

# Calculate the pooled standard deviation

pooled_std_dev &lt;- sqrt(((n1-1) * std_dev1^2 + (n2-1) * std_dev2^2) / (n1 + n2 - 2))

# Calculate the standard error of the difference

se &lt;- pooled_std_dev * sqrt((1/n1) + (1/n2))

# Calculate the t-statistic

t_statistic &lt;- (mean1 - mean2 - null_mean) / se

# Degrees of freedom

df &lt;- n1 + n2 - 2

# Calculate the critical value at a given alpha level (right-tailed test)

alpha &lt;- {{ calculationData.significanceLevel }}

critical_value &lt;- qt(1 - alpha, df=df)

# Calculate the p-value

p_value &lt;- pt(t_statistic, df=df, lower.tail=FALSE)

# Print the results

cat("Mean Difference:", mean1 - mean2, "\n")

cat("Standard Error:", se, "\n")

cat("T-Statistic:", t_statistic, "\n")

cat("P-Value:", p_value, "\n")

cat("Critical Value:", critical_value, "\n")

# Make a decision based on the p-value and critical value

if (p_value &lt; alpha && t_statistic > 0) {

  cat("Reject the null hypothesis. There is evidence of a significant increase in the mean difference.\n")

} else {

  cat("Fail to reject the null hypothesis. There is no evidence of a significant increase in the mean difference.\n")

}
</code></pre>
            </div>

            <!-- Unequal -->
            <div v-if="calculationData.answer === 'No' && calculationData.sampleAssumption === 'Unequal'">
              <!-- two tails -->
              <pre
                v-if="calculationData.method === 'readyData' && calculationData.answer === 'No' && currentTail === 'two-tailed'"
                class="language-r"
              ><code id="r">
# Calculate the sample means

mean1 &lt;- {{ calculationData.meanBefore }}

mean2 &lt;- {{ calculationData.meanAfter }}

# Calculate the sample standard deviations

std_dev1 &lt;- {{ calculationData.sdOne }}

std_dev2 &lt;- {{ calculationData.sdTwo }}

# Sample sizes

n1 &lt;- {{ calculationData.sizeOne }}

n2 &lt;- {{ calculationData.sizeTwo }}

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is not equal to zero

null_mean &lt;- {{ calculationData.expectedDifference }}

# Calculate the standard error of the difference assuming unequal variances

se &lt;- sqrt((std_dev1^2 / n1) + (std_dev2^2 / n2))

# Calculate the degrees of freedom assuming unequal variances

df &lt;- (std_dev1^2 / n1 + std_dev2^2 / n2)^2 / ((std_dev1^2 / n1)^2 / (n1 - 1) + (std_dev2^2 / n2)^2 / (n2 - 1))

# Calculate the t-statistic assuming unequal variances

t_statistic &lt;- (mean1 - mean2 - null_mean) / se

# Calculate the critical values at a given alpha level (two-tailed test)

alpha &lt;- {{ calculationData.significanceLevel }}

critical_value_left &lt;- qt(alpha / 2, df=df)

critical_value_right &lt;- qt(1 - alpha / 2, df=df)

# Calculate the p-value assuming unequal variances

p_value &lt;- 2 * pt(abs(t_statistic), df=df, lower.tail=FALSE)

# Print the results

cat("Mean Difference:", mean1 - mean2, "\n")

cat("Standard Error:", se, "\n")

cat("T-Statistic:", t_statistic, "\n")

cat("P-Value:", p_value, "\n")

cat("Critical Values:", critical_value_left, critical_value_right, "\n")

# Make a decision based on the p-value and critical values

if (p_value &lt; alpha) {

  if (t_statistic &lt; 0) {

    cat("Reject the null hypothesis. There is evidence of a significant difference between the two samples, with Sample 1 mean being less than Sample 2 mean.\n")

  } else {

    cat("Reject the null hypothesis. There is evidence of a significant difference between the two samples, with Sample 1 mean being greater than Sample 2 mean.\n")

  }

} else {

  cat("Fail to reject the null hypothesis. There is no evidence of a significant difference between the two samples.\n")

}
</code></pre>
              <pre
                v-if="calculationData.method === 'rawData' && calculationData.answer === 'No' && currentTail === 'two-tailed'"
                class="language-r"
              ><code id="r">
# Sample 1

sample1 &lt;- c({{ inputToNumbers[1][0] }})

# Sample 2

sample2 &lt;- c({{ inputToNumbers[1][1] }})

# Calculate the sample means

mean1 &lt;- mean(sample1)

mean2 &lt;- mean(sample2)

# Calculate the sample standard deviations

std_dev1 &lt;- sd(sample1)

std_dev2 &lt;- sd(sample2)

# Sample sizes

n1 &lt;- length(sample1)

n2 &lt;- length(sample2)

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is not equal to zero

null_mean &lt;- {{ calculationData.expectedDifference }}

# Calculate the standard error of the difference assuming unequal variances

se &lt;- sqrt((std_dev1^2 / n1) + (std_dev2^2 / n2))

# Calculate the degrees of freedom assuming unequal variances

df &lt;- (std_dev1^2 / n1 + std_dev2^2 / n2)^2 / ((std_dev1^2 / n1)^2 / (n1 - 1) + (std_dev2^2 / n2)^2 / (n2 - 1))

# Calculate the t-statistic assuming unequal variances

t_statistic &lt;- (mean1 - mean2 - null_mean) / se

# Calculate the critical values at a given alpha level (two-tailed test)

alpha &lt;- {{ calculationData.significanceLevel }}

critical_value_left &lt;- qt(alpha / 2, df=df)

critical_value_right &lt;- qt(1 - alpha / 2, df=df)

# Calculate the p-value assuming unequal variances

p_value &lt;- 2 * pt(abs(t_statistic), df=df, lower.tail=FALSE)

# Print the results

cat("Mean Difference:", mean1 - mean2, "\n")

cat("Standard Error:", se, "\n")

cat("T-Statistic:", t_statistic, "\n")

cat("P-Value:", p_value, "\n")

cat("Critical Values:", critical_value_left, critical_value_right, "\n")

# Make a decision based on the p-value and critical values

if (p_value &lt; alpha) {

  if (t_statistic &lt; 0) {

    cat("Reject the null hypothesis. There is evidence of a significant difference between the two samples, with Sample 1 mean being less than Sample 2 mean.\n")

  } else {

    cat("Reject the null hypothesis. There is evidence of a significant difference between the two samples, with Sample 1 mean being greater than Sample 2 mean.\n")

  }

} else {

  cat("Fail to reject the null hypothesis. There is no evidence of a significant difference between the two samples.\n")

}
</code></pre>

              <!-- left tail -->
              <pre
                v-if="calculationData.method === 'readyData' && calculationData.answer === 'No' && currentTail === 'left-tailed'"
                class="language-r"
              ><code id="r">
# Calculate the sample means

mean1 &lt;- {{ calculationData.meanBefore }}

mean2 &lt;- {{ calculationData.meanAfter }}

# Calculate the sample standard deviations

std_dev1 &lt;- {{ calculationData.sdOne }}

std_dev2 &lt;- {{ calculationData.sdTwo }}

# Sample sizes

n1 &lt;- {{ calculationData.sizeOne }}

n2 &lt;- {{ calculationData.sizeTwo }}

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is less than zero

null_mean &lt;- {{ calculationData.expectedDifference }}

# Calculate the standard error of the difference assuming unequal variances

se &lt;- sqrt((std_dev1^2 / n1) + (std_dev2^2 / n2))

# Calculate the degrees of freedom assuming unequal variances

df &lt;- (std_dev1^2 / n1 + std_dev2^2 / n2)^2 / ((std_dev1^2 / n1)^2 / (n1 - 1) + (std_dev2^2 / n2)^2 / (n2 - 1))

# Calculate the t-statistic assuming unequal variances

t_statistic &lt;- (mean1 - mean2 - null_mean) / se

# Calculate the critical value at a given alpha level (left-tailed test)

alpha &lt;- {{ calculationData.significanceLevel }}

critical_value &lt;- qt(alpha, df=df, lower.tail=TRUE)

# Calculate the p-value assuming unequal variances

p_value &lt;- pt(t_statistic, df=df, lower.tail=TRUE)

# Print the results

cat("Mean Difference:", mean1 - mean2, "\n")

cat("Standard Error:", se, "\n")

cat("T-Statistic:", t_statistic, "\n")

cat("P-Value:", p_value, "\n")

cat("Critical Value:", critical_value, "\n")

# Make a decision based on the p-value and critical value

if (p_value &lt; alpha && t_statistic &lt; 0) {

  cat("Reject the null hypothesis. There is evidence of a significant difference between the two samples, with Sample 1 mean being less than Sample 2 mean.\n")

} else {

  cat("Fail to reject the null hypothesis. There is no evidence of a significant difference between the two samples.\n")

}
</code></pre>
              <pre
                v-if="calculationData.method === 'rawData' && calculationData.answer === 'No' && currentTail === 'left-tailed'"
                class="language-r"
              ><code id="r">
# Sample 1

sample1 &lt;- c({{ inputToNumbers[1][0] }})

# Sample 2

sample2 &lt;- c({{ inputToNumbers[1][1] }})

# Calculate the sample means

mean1 &lt;- mean(sample1)

mean2 &lt;- mean(sample2)

# Calculate the sample standard deviations

std_dev1 &lt;- sd(sample1)

std_dev2 &lt;- sd(sample2)

# Sample sizes

n1 &lt;- length(sample1)

n2 &lt;- length(sample2)

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is less than zero

null_mean &lt;- {{ calculationData.expectedDifference }}

# Calculate the standard error of the difference assuming unequal variances

se &lt;- sqrt((std_dev1^2 / n1) + (std_dev2^2 / n2))

# Calculate the degrees of freedom assuming unequal variances

df &lt;- (std_dev1^2 / n1 + std_dev2^2 / n2)^2 / ((std_dev1^2 / n1)^2 / (n1 - 1) + (std_dev2^2 / n2)^2 / (n2 - 1))

# Calculate the t-statistic assuming unequal variances

t_statistic &lt;- (mean1 - mean2 - null_mean) / se

# Calculate the critical value at a given alpha level (left-tailed test)

alpha &lt;- {{ calculationData.significanceLevel }}

critical_value &lt;- qt(alpha, df=df, lower.tail=TRUE)

# Calculate the p-value assuming unequal variances

p_value &lt;- pt(t_statistic, df=df, lower.tail=TRUE)

# Print the results

cat("Mean Difference:", mean1 - mean2, "\n")

cat("Standard Error:", se, "\n")

cat("T-Statistic:", t_statistic, "\n")

cat("P-Value:", p_value, "\n")

cat("Critical Value:", critical_value, "\n")

# Make a decision based on the p-value and critical value

if (p_value &lt; alpha && t_statistic &lt; 0) {

  cat("Reject the null hypothesis. There is evidence of a significant difference between the two samples, with Sample 1 mean being less than Sample 2 mean.\n")

} else {

  cat("Fail to reject the null hypothesis. There is no evidence of a significant difference between the two samples.\n")

}
</code></pre>

              <!-- right tail -->
              <pre
                v-if="calculationData.method === 'readyData' && calculationData.answer === 'No' && currentTail === 'right-tailed'"
                class="language-r"
              ><code id="r">
# Calculate the sample means

mean1 &lt;- {{ calculationData.meanBefore }}

mean2 &lt;- {{ calculationData.meanAfter }}

# Calculate the sample standard deviations

std_dev1 &lt;- {{ calculationData.sdOne }}

std_dev2 &lt;- {{ calculationData.sdTwo }}

# Sample sizes

n1 &lt;- {{ calculationData.sizeOne }}

n2 &lt;- {{ calculationData.sizeTwo }}

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is greater than zero

null_mean &lt;- {{ calculationData.expectedDifference }}

# Calculate the standard error of the difference assuming unequal variances

se &lt;- sqrt((std_dev1^2 / n1) + (std_dev2^2 / n2))

# Calculate the degrees of freedom assuming unequal variances

df &lt;- (std_dev1^2 / n1 + std_dev2^2 / n2)^2 / ((std_dev1^2 / n1)^2 / (n1 - 1) + (std_dev2^2 / n2)^2 / (n2 - 1))

# Calculate the t-statistic assuming unequal variances

t_statistic &lt;- (mean1 - mean2 - null_mean) / se

# Calculate the critical value at a given alpha level (right-tailed test)

alpha &lt;- {{ calculationData.significanceLevel }}

critical_value &lt;- qt(1 - alpha, df=df, lower.tail=FALSE)

# Calculate the p-value assuming unequal variances

p_value &lt;- pt(t_statistic, df=df, lower.tail=FALSE)

# Print the results

cat("Mean Difference:", mean1 - mean2, "\n")

cat("Standard Error:", se, "\n")

cat("T-Statistic:", t_statistic, "\n")

cat("P-Value:", p_value, "\n")

cat("Critical Value:", critical_value, "\n")

# Make a decision based on the p-value and critical value

if (p_value &lt; alpha && t_statistic > 0) {

  cat("Reject the null hypothesis. There is evidence of a significant difference between the two samples, with Sample 1 mean being greater than Sample 2 mean.\n")

} else {

  cat("Fail to reject the null hypothesis. There is no evidence of a significant difference between the two samples.\n")

}
</code></pre>
              <pre
                v-if="calculationData.method === 'rawData' && calculationData.answer === 'No' && currentTail === 'right-tailed'"
                class="language-r"
              ><code id="r">
# Sample 1

sample1 &lt;- c({{ inputToNumbers[1][0] }})

# Sample 2

sample2 &lt;- c({{ inputToNumbers[1][1] }})

# Calculate the sample means

mean1 &lt;- mean(sample1)

mean2 &lt;- mean(sample2)

# Calculate the sample standard deviations

std_dev1 &lt;- sd(sample1)

std_dev2 &lt;- sd(sample2)

# Sample sizes

n1 &lt;- length(sample1)

n2 &lt;- length(sample2)

# Null hypothesis: The mean difference is zero

# Alternative hypothesis: The mean difference is greater than zero

null_mean &lt;- {{ calculationData.expectedDifference }}

# Calculate the standard error of the difference assuming unequal variances

se &lt;- sqrt((std_dev1^2 / n1) + (std_dev2^2 / n2))

# Calculate the degrees of freedom assuming unequal variances

df &lt;- (std_dev1^2 / n1 + std_dev2^2 / n2)^2 / ((std_dev1^2 / n1)^2 / (n1 - 1) + (std_dev2^2 / n2)^2 / (n2 - 1))

# Calculate the t-statistic assuming unequal variances

t_statistic &lt;- (mean1 - mean2 - null_mean) / se

# Calculate the critical value at a given alpha level (right-tailed test)

alpha &lt;- {{ calculationData.significanceLevel }}

critical_value &lt;- qt(1 - alpha, df=df, lower.tail=FALSE)

# Calculate the p-value assuming unequal variances

p_value &lt;- pt(t_statistic, df=df, lower.tail=FALSE)

# Print the results

cat("Mean Difference:", mean1 - mean2, "\n")

cat("Standard Error:", se, "\n")

cat("T-Statistic:", t_statistic, "\n")

cat("P-Value:", p_value, "\n")

cat("Critical Value:", critical_value, "\n")

# Make a decision based on the p-value and critical value

if (p_value &lt; alpha && t_statistic > 0) {

  cat("Reject the null hypothesis. There is evidence of a significant difference between the two samples, with Sample 1 mean being greater than Sample 2 mean.\n")

} else {

  cat("Fail to reject the null hypothesis. There is no evidence of a significant difference between the two samples.\n")

}
</code></pre>
            </div>
          </div>
        </div>
      </div>
      <button class="btn-link-accent" :class="{ copied: copyCodeOrCopiedR === 'Copied' }" @click="copyCode('r')">
        {{ copyCodeOrCopiedR }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  data() {
    return {
      copyCodeOrCopiedPython: 'Copy Code',
      copyCodeOrCopiedR: 'Copy Code',
    };
  },
  computed: {
    ...mapState('calculators', ['result', 'calculationData']),
    significanceLevelDividedByTwo() {
      return this.calculationData.significanceLevel / 2;
    },
    currentConfidenceLevel() {
      return (1 - this.calculationData.significanceLevel).toFixed(2);
    },
    currentStatistic() {
      return this.calculationData.answer === 'Yes' && this.calculationData.size >= 30 ? ['z', 'Z', 'σ'] : ['t', 'T', 's'];
    },
    currentTable() {
      return this.calculationData.answer === 'Yes' ? 'standard normal distribution table' : 'Student’s t table';
    },
    currentTail() {
      if (this.calculationData.operator[0] === '=') {
        return 'two-tailed';
      }

      if (this.calculationData.operator[1] === '>') {
        return 'right-tailed';
      }

      return 'left-tailed';
    },
    currentDegreesOfFreedom() {
      return this.calculationData.answer === 'No' && this.calculationData.size < 30 ? `${this.calculationData.degreesOfFreedom},` : '';
    },
    degreesOfFreedomToSize() {
      return this.calculationData.degreesOfFreedom + 1;
    },
    pValueSignificanceLevelComparison() {
      if (this.calculationData.pValue <= this.calculationData.significanceLevel) {
        return '≤';
      }

      return '>';
    },
    valuesApproach() {
      return {
        critical: {
          'two-tailed': this.calculationData.testStatistic <= (this.calculationData.currentScore * -1) || this.calculationData.testStatistic >= this.calculationData.currentScore,
          'left-tailed': this.calculationData.testStatistic <= -Math.abs(this.calculationData.currentScore),
          'right-tailed': this.calculationData.testStatistic >= this.calculationData.currentScore,
        },
        'p-value': {
          'two-tailed': parseFloat(this.calculationData.pValue) <= this.calculationData.significanceLevel,
          'left-tailed': parseFloat(this.calculationData.pValue) <= this.calculationData.significanceLevel,
          'right-tailed': parseFloat(this.calculationData.pValue) <= this.calculationData.significanceLevel,
        },
      };
    },
    inputToNumbers() {
      return [
        [this.calculationData.inputArrays[0].map((str) => Number(str)), this.calculationData.inputArrays[1].map((str) => Number(str))],
        [this.calculationData.inputArrays[0].map((str) => Number(str)).join(', '), this.calculationData.inputArrays[1].map((str) => Number(str)).join(', ')],
      ];
    },
  },
  methods: {
    copyCode(id) {
      if (id.split('-')[0] === 'python') {
        this.copyCodeOrCopiedPython = 'Copied';
        setTimeout(() => {
          this.copyCodeOrCopiedPython = 'Copy Code';
        }, 1000);
      } else {
        this.copyCodeOrCopiedR = 'Copied';
        setTimeout(() => {
          this.copyCodeOrCopiedR = 'Copy Code';
        }, 1000);
      }

      const currentText = document.getElementById(id).innerText;
      this.$copy(currentText);
    },
  },
};
</script>

<style lang="scss" scoped>
.calculation-steps {
  p,
  .no-margin.no-margin {
    margin: 0;
  }

  .top-bottom-margins.top-bottom-margins {
    margin: var(--spacing-16) 0;
  }

  ::v-deep .bolded-texts-v-html {
    font-weight: 600;
  }

  .python-r-codes.python {
    margin-top: var(--spacing-80);
  }

  ol {
    li {
      margin-top: var(--spacing-32);

      &:nth-of-type(2) {
        margin-bottom: var(--spacing-16);
      }

      &:nth-of-type(4) {
        margin: var(--spacing-16) 0;
      }

      &:nth-of-type(5),
      &:nth-of-type(6),
      &:nth-of-type(3) {
        margin: var(--spacing-16) 0 0;
      }
    }
  }

  ul {
    li {
      &:nth-of-type(1),
      &:nth-of-type(2) {
        margin: var(--spacing-16) 0 0;
      }
    }
  }

  .ul-big {
    margin-bottom: var(--spacing-16);
  }
}

@include bp($bp-mobile) {
  div .calculation-steps {
    padding-top: var(--spacing-40);

    .h4 {
      text-align: left;
    }

    .top-margin-mobile.top-margin-mobile {
      margin-top: var(--spacing-10);
    }
  }
}

@import '../../../../assets/sass/ds/calculators/calculation-steps.scss';
</style>
